import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  usersAPI, productsAPI, warehousesAPI, ordersAPI, 
  bankAccountsAPI, onlinePaymentAPI, creditCardSystemsAPI, 
  discountSettingsAPI, brandsAPI, quickSalesAPI, adminSettingsAPI, priceListAPI 
} from '../services/api';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { 
  Search, Package, ClipboardList, Users, Settings, LogOut, Menu, X, 
  FileSpreadsheet, Download, Upload, Trash2, CheckCircle, XCircle, Eye,
  BarChart3, TrendingUp, ShoppingBag, DollarSign, Plus, Edit, Save, Percent,
  User, Truck, Building2, FileText, Bell, CreditCard, ExternalLink, Image, RefreshCw, ChevronDown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SayimSection from '../components/SayimSection';

// Helper functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY'
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const AdminDashboard = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userFilter, setUserFilter] = useState('all');
  const [orderFilter, setOrderFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Data states
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [brands, setBrands] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [creditCardSystems, setCreditCardSystems] = useState([]);
  const [onlinePayment, setOnlinePayment] = useState({ link: '', password: '', description: '', is_active: false });
  const [discountSettings, setDiscountSettings] = useState({ iskonto1: 10, iskonto2: 5, iskonto3: 8, iskonto4: 12 });

  // Form states
  const [newWarehouse, setNewWarehouse] = useState('');
  const [newBankAccount, setNewBankAccount] = useState({ bank_name: '', account_name: '', iban: '', branch: '', is_active: true });
  const [newCreditCardSystem, setNewCreditCardSystem] = useState({ name: '', api_key: '', secret_key: '', is_active: true });
  const [adminSettings, setAdminSettings] = useState({ username: user?.username || 'admin', password: '', newPassword: '', confirmPassword: '' });

  // Edit dialogs
  const [editingUser, setEditingUser] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showNewProductDialog, setShowNewProductDialog] = useState(false);
  const [newProduct, setNewProduct] = useState({
    code: '', name: '', brand: '', dot: '2024', season: 'Yaz',
    base_price: '', iskonto1: 0, iskonto2: 0, iskonto3: 0, iskonto4: 0,
    image: '', stock: {}
  });

  // Product filter states
  const [productWarehouseFilter, setProductWarehouseFilter] = useState('all');
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productStockFilter, setProductStockFilter] = useState('all');
  const [productStockStatus, setProductStockStatus] = useState('all'); // 'all', 'inStock', 'outOfStock'
  const [selectedProducts, setSelectedProducts] = useState([]); // Toplu işlem için seçili ürünler

  // Sayım states
  const [sayimWarehouse, setSayimWarehouse] = useState('');
  const [sayimSearch, setSayimSearch] = useState('');
  const [sayimData, setSayimData] = useState({});
  const [sayimQuickAdd, setSayimQuickAdd] = useState('');

  // Hızlı Satış states
  const [quickSaleDate, setQuickSaleDate] = useState(new Date().toISOString().split('T')[0]);
  const [quickSaleCustomer, setQuickSaleCustomer] = useState('');
  const [quickSaleDescription, setQuickSaleDescription] = useState('');
  const [quickSaleWarehouse, setQuickSaleWarehouse] = useState('');
  const [quickSaleSearch, setQuickSaleSearch] = useState('');
  const [quickSaleItems, setQuickSaleItems] = useState([]);
  const [quickSaleShowProducts, setQuickSaleShowProducts] = useState(false);
  const [quickSaleHistory, setQuickSaleHistory] = useState([]);
  const [quickSaleHistoryStartDate, setQuickSaleHistoryStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [quickSaleHistoryEndDate, setQuickSaleHistoryEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Fiyatlama states
  const [pricingBrandDiscounts, setPricingBrandDiscounts] = useState({});
  const [pricingExpandedBrand, setPricingExpandedBrand] = useState(null);

  // Toplu Görsel states
  const [bulkImageSearch, setBulkImageSearch] = useState('');
  const [bulkImageUrl, setBulkImageUrl] = useState('');
  const [bulkImageExpandedGroup, setBulkImageExpandedGroup] = useState(null);
  const [bulkImageLoading, setBulkImageLoading] = useState(false);

  // Genel Liste Fiyatları states
  const [priceList, setPriceList] = useState([]);
  const [priceListSearch, setPriceListSearch] = useState('');
  const [priceListLoading, setPriceListLoading] = useState(false);
  const priceListFileInputRef = useRef(null);

  // Ürün Yönetimi Resim Göster/Gizle ayarı (Veritabanında - her cihazda aynı)
  const [showProductImages, setShowProductImages] = useState(true);
  const [showImagesBireysel, setShowImagesBireysel] = useState(true);
  const [showImagesToptanci, setShowImagesToptanci] = useState(true);
  const [adminSettingsLoaded, setAdminSettingsLoaded] = useState(false);

  // Admin ayarlarını veritabanından yükle
  const loadAdminSettings = useCallback(async () => {
    try {
      const res = await adminSettingsAPI.get();
      if (res.data) {
        setShowProductImages(res.data.showProductImages !== false);
        setShowImagesBireysel(res.data.showImagesBireysel !== false);
        setShowImagesToptanci(res.data.showImagesToptanci !== false);
      }
      setAdminSettingsLoaded(true);
    } catch (error) {
      console.error('Admin settings load error:', error);
      setAdminSettingsLoaded(true);
    }
  }, []);

  // Resim ayarını değiştir ve veritabanına kaydet
  const toggleShowProductImages = async (value) => {
    setShowProductImages(value);
    try {
      await adminSettingsAPI.update({ showProductImages: value });
    } catch (error) {
      console.error('Admin settings save error:', error);
    }
  };

  // Bireysel görsel ayarını değiştir
  const toggleShowImagesBireysel = async (value) => {
    setShowImagesBireysel(value);
    try {
      await adminSettingsAPI.update({ showImagesBireysel: value });
    } catch (error) {
      console.error('Admin settings save error:', error);
    }
  };

  // Toptancı görsel ayarını değiştir
  const toggleShowImagesToptanci = async (value) => {
    setShowImagesToptanci(value);
    try {
      await adminSettingsAPI.update({ showImagesToptanci: value });
    } catch (error) {
      console.error('Admin settings save error:', error);
    }
  };

  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [usersRes, productsRes, ordersRes, warehousesRes, brandsRes, bankRes, ccRes, opRes, discRes, quickSalesRes] = await Promise.all([
        usersAPI.getAll().catch(() => ({ data: [] })),
        productsAPI.getAll().catch(() => ({ data: [] })),
        ordersAPI.getAll().catch(() => ({ data: [] })),
        warehousesAPI.getAll().catch(() => ({ data: [] })),
        brandsAPI.getAll().catch(() => ({ data: [] })),
        bankAccountsAPI.getAll().catch(() => ({ data: [] })),
        creditCardSystemsAPI.getAll().catch(() => ({ data: [] })),
        onlinePaymentAPI.get().catch(() => ({ data: { link: '', password: '', description: '', is_active: false } })),
        discountSettingsAPI.get().catch(() => ({ data: { iskonto1: 10, iskonto2: 5, iskonto3: 8, iskonto4: 12 } })),
        quickSalesAPI.getAll().catch(() => ({ data: [] }))
      ]);
      
      setUsers(usersRes.data || []);
      setProducts(productsRes.data || []);
      setOrders(ordersRes.data || []);
      setWarehouses(warehousesRes.data || []);
      setBrands(brandsRes.data || []);
      setBankAccounts(bankRes.data || []);
      setCreditCardSystems(ccRes.data || []);
      setOnlinePayment(opRes.data || { link: '', password: '', description: '', is_active: false });
      setDiscountSettings(discRes.data || { iskonto1: 10, iskonto2: 5, iskonto3: 8, iskonto4: 12 });
      setQuickSaleHistory(quickSalesRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setIsLoading(false);
  }, []);

  // Genel Liste Fiyatları yükleme
  const loadPriceList = useCallback(async () => {
    try {
      const res = await priceListAPI.getAll();
      setPriceList(res.data || []);
    } catch (error) {
      console.error('Error loading price list:', error);
    }
  }, []);

  useEffect(() => {
    const initLoad = async () => {
      await loadData();
      await loadAdminSettings();
      await loadPriceList();
    };
    initLoad();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Hızlı Satış fonksiyonları
  const quickSaleFilteredProducts = useMemo(() => {
    if (!quickSaleSearch.trim()) return [];
    const searchLower = quickSaleSearch.toLowerCase().trim();
    const searchTokens = searchLower.replace(/[\/\-_,\.]/g, ' ').split(/\s+/).filter(t => t.length > 0);
    
    return products.filter(p => {
      const productText = [p.name, p.code, p.brand, p.season, p.dot].join(' ').toLowerCase().replace(/[\/\-_,\.]/g, ' ');
      const matchesSearch = searchTokens.every(token => productText.includes(token));
      const hasStock = quickSaleWarehouse ? (p.stock?.[quickSaleWarehouse] || 0) > 0 : true;
      return matchesSearch && hasStock;
    }).slice(0, 10);
  }, [products, quickSaleSearch, quickSaleWarehouse]);

  // Ürün Yönetimi için filtreleme (Hızlı Satış gibi akıllı arama)
  const filteredProductsForManagement = useMemo(() => {
    let filtered = products;
    
    // Depo filtresi
    if (productWarehouseFilter !== 'all') {
      filtered = filtered.filter(p => (p.stock?.[productWarehouseFilter] || 0) > 0);
    }
    
    // Stok durumu filtresi (stokta var/yok)
    if (productStockStatus === 'inStock') {
      filtered = filtered.filter(p => {
        const totalStock = Object.values(p.stock || {}).reduce((a, b) => a + b, 0);
        return totalStock > 0;
      });
    } else if (productStockStatus === 'outOfStock') {
      filtered = filtered.filter(p => {
        const totalStock = Object.values(p.stock || {}).reduce((a, b) => a + b, 0);
        return totalStock === 0;
      });
    }
    
    // Stok adet filtresi
    if (productStockFilter !== 'all') {
      const stockAmount = parseInt(productStockFilter);
      filtered = filtered.filter(p => {
        const totalStock = Object.values(p.stock || {}).reduce((a, b) => a + b, 0);
        return totalStock === stockAmount;
      });
    }
    
    // Arama filtresi
    if (productSearchTerm.trim()) {
      const searchLower = productSearchTerm.toLowerCase().trim();
      
      // Arama terimlerini ayır
      const searchTokens = searchLower.replace(/[\/\-_,\.]/g, ' ').split(/\s+/).filter(t => t.length > 0);
      
      filtered = filtered.filter(p => {
        // Ürün bilgileri
        const productText = [p.name, p.code, p.brand, p.season, p.dot].join(' ').toLowerCase();
        const productTextNormalized = productText.replace(/[\/\-_,\.]/g, ' ');
        const productDigitsOnly = [p.name, p.code].join(' ').replace(/[^0-9]/g, '');
        
        // Her arama terimi için kontrol et
        // Tüm terimler eşleşmeli (AND mantığı)
        return searchTokens.every(token => {
          // Terim sadece rakamlardan mı oluşuyor?
          const tokenDigitsOnly = token.replace(/[^0-9]/g, '');
          const isDigitToken = tokenDigitsOnly.length >= 5 && tokenDigitsOnly === token;
          
          if (isDigitToken) {
            // Birleşik rakam araması (örn: 2055516)
            // Ürün rakamlarında bu rakam dizisi var mı?
            return productDigitsOnly.includes(tokenDigitsOnly);
          } else {
            // Normal metin araması
            return productTextNormalized.includes(token);
          }
        });
      });
    }
    
    return filtered;
  }, [products, productWarehouseFilter, productSearchTerm, productStockFilter, productStockStatus]);

  // Benzersiz stok adetleri listesi
  const uniqueStockAmounts = useMemo(() => {
    const stockSet = new Set();
    stockSet.add(0); // 0 stokları da ekle
    products.forEach(p => {
      const totalStock = Object.values(p.stock || {}).reduce((a, b) => a + b, 0);
      stockSet.add(totalStock);
    });
    return [...stockSet].sort((a, b) => a - b);
  }, [products]);

  // Marka ve kategori analizi - ürünlerden otomatik çıkar
  const brandCategories = useMemo(() => {
    const categories = {};
    
    products.forEach(product => {
      const brand = (product.brand || '').toUpperCase().trim();
      if (!brand) return;
      
      if (!categories[brand]) {
        categories[brand] = {
          brand,
          subcategories: {},
          products: [],
          totalProducts: 0
        };
      }
      
      // Ürün isminden SONDAKİ parantez içindeki kategoriyi çıkar
      const nameUpper = (product.name || '').toUpperCase();
      const matches = nameUpper.match(/\(([^)]+)\)/g);
      const lastMatch = matches && matches.length > 0 ? matches[matches.length - 1] : null;
      const subcategory = lastMatch ? lastMatch.replace(/[()]/g, '').trim() : 'GENEL';
      
      if (!categories[brand].subcategories[subcategory]) {
        categories[brand].subcategories[subcategory] = {
          name: subcategory,
          products: [],
          iskonto1: 0,
          iskonto2: 0,
          iskonto3: 0,
          iskonto4: 0
        };
      }
      
      categories[brand].subcategories[subcategory].products.push(product);
      categories[brand].products.push(product);
      categories[brand].totalProducts++;
    });
    
    return categories;
  }, [products]);

  // Toplu Görsel - Ürünleri MODEL ADINA göre grupla (REVOLA, KNK30, SNOWAYS, vb.)
  const productNameGroups = useMemo(() => {
    const groups = {};
    
    products.forEach(product => {
      const name = (product.name || '').trim().toUpperCase();
      if (!name) return;
      
      // Ürün isminden MODEL ADINI çıkar
      // Örnek: "LASSA REVOLA 205/55R16 (YAZ)" → "REVOLA"
      // Örnek: "PETLAS KNK30 185/65R15" → "KNK30"
      // Örnek: "MAXIWAYS 100 225/45R17" → "MAXIWAYS 100"
      
      // 1. Parantez içindeki kısımları temizle
      let cleanName = name.replace(/\([^)]*\)/g, '').trim();
      
      // 2. Lastik boyutu pattern'ini bul (örn: 205/55R16, 185/65R15, 225/45ZR17)
      const sizePattern = /\d{2,3}\/\d{2,3}\s*[ZR]*\d{2,3}/i;
      const sizeMatch = cleanName.match(sizePattern);
      
      let modelName = '';
      
      if (sizeMatch) {
        // Boyut bilgisinden ÖNCE gelen kısmı al
        const beforeSize = cleanName.substring(0, sizeMatch.index).trim();
        
        // Marka adını çıkarmaya çalış (ilk kelime genellikle marka)
        const words = beforeSize.split(/\s+/);
        
        if (words.length > 1) {
          // İlk kelime marka, geri kalanı model
          // Örn: "LASSA REVOLA" → "REVOLA"
          // Örn: "PETLAS SNOWAYS ERA" → "SNOWAYS ERA"
          modelName = words.slice(1).join(' ');
        } else {
          // Tek kelime varsa o model adıdır
          modelName = words[0];
        }
      } else {
        // Boyut bulunamadıysa, tüm ismi model olarak al (parantez hariç)
        const words = cleanName.split(/\s+/);
        if (words.length > 1) {
          modelName = words.slice(1).join(' ');
        } else {
          modelName = words[0];
        }
      }
      
      // Model adını temizle
      modelName = modelName.replace(/\s+/g, ' ').trim();
      
      // Boş veya çok kısa ise tüm ismi kullan
      if (!modelName || modelName.length < 2) {
        modelName = cleanName.split(/\s+/).slice(0, 2).join(' ');
      }
      
      if (!groups[modelName]) {
        groups[modelName] = {
          baseName: modelName,
          displayName: modelName,
          products: [],
          hasImage: false,
          brands: new Set()
        };
      }
      
      groups[modelName].products.push(product);
      groups[modelName].brands.add(product.brand || '');
      if (product.image) {
        groups[modelName].hasImage = true;
      }
    });
    
    // Set'leri array'e çevir ve sırala
    return Object.values(groups)
      .map(g => ({ ...g, brands: [...g.brands].filter(b => b).join(', ') }))
      .sort((a, b) => b.products.length - a.products.length);
  }, [products]);

  // Toplu Görsel - Filtrelenmiş gruplar
  const filteredProductGroups = useMemo(() => {
    if (!bulkImageSearch.trim()) return productNameGroups;
    
    const searchLower = bulkImageSearch.toLowerCase();
    return productNameGroups.filter(group => 
      group.baseName.toLowerCase().includes(searchLower) ||
      group.products.some(p => 
        p.name?.toLowerCase().includes(searchLower) ||
        p.code?.toLowerCase().includes(searchLower) ||
        p.brand?.toLowerCase().includes(searchLower)
      )
    );
  }, [productNameGroups, bulkImageSearch]);

  // Toplu Görsel - Gruba görsel ekle
  const applyBulkImage = async (groupBaseName, imageUrl) => {
    const group = productNameGroups.find(g => g.baseName === groupBaseName);
    if (!group || !imageUrl.trim()) {
      alert('Lütfen geçerli bir görsel linki girin!');
      return;
    }
    
    setBulkImageLoading(true);
    try {
      let successCount = 0;
      for (const product of group.products) {
        const updatedProduct = { ...product, image: imageUrl.trim() };
        await productsAPI.update(product.id, updatedProduct);
        successCount++;
      }
      await loadData();
      setBulkImageUrl('');
      alert(`${successCount} ürüne görsel başarıyla eklendi!`);
    } catch (error) {
      console.error('Bulk image error:', error);
      alert('Görsel eklenirken hata oluştu!');
    }
    setBulkImageLoading(false);
  };

  // Toplu Görsel - Gruba görünürlük ayarla (Göster/Gösterme)
  const applyBulkVisibility = async (groupBaseName, field, value) => {
    const group = productNameGroups.find(g => g.baseName === groupBaseName);
    if (!group) return;
    
    const actionText = value ? 'GÖSTERİLECEK' : 'GİZLENECEK';
    const fieldText = field === 'visible_bireysel' ? 'Bireysel' : 'Toptancı';
    
    if (!window.confirm(`${group.products.length} ürün ${fieldText} kullanıcılara ${actionText}. Devam etmek istiyor musunuz?`)) {
      return;
    }
    
    setBulkImageLoading(true);
    try {
      let successCount = 0;
      for (const product of group.products) {
        const updatedProduct = { ...product, [field]: value };
        await productsAPI.update(product.id, updatedProduct);
        successCount++;
      }
      await loadData();
      alert(`${successCount} ürün güncellendi!`);
    } catch (error) {
      console.error('Bulk visibility error:', error);
      alert('Görünürlük güncellenirken hata oluştu!');
    }
    setBulkImageLoading(false);
  };

  // TÜM ürünlere toplu görünürlük ayarla (Tek tık ile hepsini Göster/Gösterme)
  const applyAllProductsVisibility = async (field, value) => {
    const actionText = value ? 'GÖSTERİLECEK' : 'GİZLENECEK';
    const fieldText = field === 'visible_bireysel' ? 'Bireysel' : 'Toptancı';
    
    if (!window.confirm(`TÜM ${products.length} ürün ${fieldText} kullanıcılara ${actionText}. Devam etmek istiyor musunuz?`)) {
      return;
    }
    
    setBulkImageLoading(true);
    try {
      let successCount = 0;
      for (const product of products) {
        const updatedProduct = { ...product, [field]: value };
        await productsAPI.update(product.id, updatedProduct);
        successCount++;
      }
      await loadData();
      alert(`${successCount} ürün güncellendi!`);
    } catch (error) {
      console.error('Bulk all visibility error:', error);
      alert('Görünürlük güncellenirken hata oluştu!');
    }
    setBulkImageLoading(false);
  };

  // ==================== GENEL LİSTE FİYATLARI FONKSİYONLARI ====================
  
  // Excel'den fiyat listesi yükle
  const handlePriceListUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setPriceListLoading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length < 2) {
        alert('Excel dosyası boş veya geçersiz!');
        setPriceListLoading(false);
        return;
      }
      
      // İlk satır başlıklar
      const headers = jsonData[0];
      const items = [];
      
      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || row.length === 0 || !row[0]) continue;
        
        items.push({
          stok_kodu: String(row[0] || ''),
          urun_adi: String(row[1] || ''),
          liste_fiyati: parseFloat(row[2]) || 0,
          perakende_fiyati: parseFloat(row[3]) || 0,
          toptan_fiyati: parseFloat(row[4]) || 0,
          jant_grubu: String(row[5] || '')
        });
      }
      
      if (items.length === 0) {
        alert('Geçerli veri bulunamadı!');
        setPriceListLoading(false);
        return;
      }
      
      // API'ye gönder
      const res = await priceListAPI.upload(items);
      alert(res.data.message);
      await loadPriceList();
    } catch (error) {
      console.error('Price list upload error:', error);
      alert('Excel yüklenirken hata oluştu!');
    }
    setPriceListLoading(false);
    if (priceListFileInputRef.current) {
      priceListFileInputRef.current.value = '';
    }
  };
  
  // Fiyat listesini Excel olarak indir
  const handlePriceListDownload = () => {
    if (priceList.length === 0) {
      alert('İndirilecek veri yok!');
      return;
    }
    
    const data = [
      ['Stok Kodu', 'Ürün Adı', 'Liste Fiyatı', 'Perakende Fiyatı', 'Toptan Fiyatı', 'Jant Grubu'],
      ...priceList.map(item => [
        item.stok_kodu,
        item.urun_adi,
        item.liste_fiyati,
        item.perakende_fiyati,
        item.toptan_fiyati,
        item.jant_grubu
      ])
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Liste Fiyatları');
    XLSX.writeFile(wb, `genel_liste_fiyatlari_${new Date().toISOString().split('T')[0]}.xlsx`);
  };
  
  // Fiyat listesini sil
  const handlePriceListDelete = async () => {
    if (!window.confirm(`Tüm liste fiyatları silinecek (${priceList.length} kayıt). Emin misiniz?`)) {
      return;
    }
    
    setPriceListLoading(true);
    try {
      const res = await priceListAPI.deleteAll();
      alert(res.data.message);
      setPriceList([]);
    } catch (error) {
      console.error('Price list delete error:', error);
      alert('Silme işlemi başarısız!');
    }
    setPriceListLoading(false);
  };
  
  // Filtrelenmiş fiyat listesi
  const filteredPriceList = useMemo(() => {
    if (!priceListSearch.trim()) return priceList;
    
    const searchLower = priceListSearch.toLowerCase();
    const searchDigits = priceListSearch.replace(/[^0-9]/g, '');
    
    return priceList.filter(item => {
      const textMatch = 
        item.stok_kodu?.toLowerCase().includes(searchLower) ||
        item.urun_adi?.toLowerCase().includes(searchLower) ||
        item.jant_grubu?.toLowerCase().includes(searchLower);
      
      // Birleşik rakam araması
      const itemDigits = (item.urun_adi || '').replace(/[^0-9]/g, '');
      const digitMatch = searchDigits.length >= 5 && itemDigits.includes(searchDigits);
      
      return textMatch || digitMatch;
    });
  }, [priceList, priceListSearch]);

  // Toplu iskonto uygula
  const applyBulkDiscount = async (brand, subcategory, discountType, value) => {
    const categoryData = brandCategories[brand]?.subcategories[subcategory];
    if (!categoryData) return;
    
    try {
      for (const product of categoryData.products) {
        const updatedProduct = { ...product, [discountType]: parseFloat(value) || 0 };
        await productsAPI.update(product.id, updatedProduct);
      }
      await loadData();
      alert(`${brand} - ${subcategory} için ${discountType} güncellendi: %${value}`);
    } catch (error) {
      console.error('Bulk discount error:', error);
      alert('İskonto güncellenirken hata oluştu!');
    }
  };

  // Tüm alt kategoriye toplu iskonto uygula
  const applyAllDiscountsToCategory = async (brand, subcategory, i1, i2, i3, i4) => {
    const categoryData = brandCategories[brand]?.subcategories[subcategory];
    if (!categoryData) return;
    
    try {
      for (const product of categoryData.products) {
        const updatedProduct = { 
          ...product, 
          iskonto1: parseFloat(i1) || 0,
          iskonto2: parseFloat(i2) || 0,
          iskonto3: parseFloat(i3) || 0,
          iskonto4: parseFloat(i4) || 0
        };
        await productsAPI.update(product.id, updatedProduct);
      }
      await loadData();
      alert(`${brand} - ${subcategory} için tüm iskontolar güncellendi!`);
    } catch (error) {
      console.error('Bulk discount error:', error);
      alert('İskonto güncellenirken hata oluştu!');
    }
  };

  const addQuickSaleItem = (product) => {
    const existingIndex = quickSaleItems.findIndex(item => item.product_id === product.id && item.warehouse_id === quickSaleWarehouse);
    if (existingIndex >= 0) {
      // Increase quantity for same product from same warehouse
      const newItems = [...quickSaleItems];
      newItems[existingIndex].quantity += 1;
      setQuickSaleItems(newItems);
    } else {
      // Add new item with its warehouse info
      const warehouseName = warehouses.find(w => w.id === quickSaleWarehouse)?.name || '';
      setQuickSaleItems([...quickSaleItems, {
        product_id: product.id,
        product_code: product.code,
        product_name: product.name,
        brand: product.brand,
        quantity: 1,
        unit_price: product.base_price || 0,
        warehouse_id: quickSaleWarehouse,
        warehouse_name: warehouseName,
        available_stock: product.stock?.[quickSaleWarehouse] || 0
      }]);
    }
    setQuickSaleSearch('');
    setQuickSaleShowProducts(false);
  };

  const updateQuickSaleItem = (index, field, value) => {
    const newItems = [...quickSaleItems];
    newItems[index][field] = value;
    setQuickSaleItems(newItems);
  };

  const removeQuickSaleItem = (index) => {
    setQuickSaleItems(quickSaleItems.filter((_, i) => i !== index));
  };

  const quickSaleTotal = useMemo(() => {
    return quickSaleItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  }, [quickSaleItems]);

  const handleQuickSale = async () => {
    if (quickSaleItems.length === 0) {
      alert('Lütfen en az bir ürün ekleyin!');
      return;
    }

    try {
      // Update stock for each item from its own warehouse
      for (const item of quickSaleItems) {
        const product = products.find(p => p.id === item.product_id);
        if (product && item.warehouse_id) {
          const currentStock = product.stock?.[item.warehouse_id] || 0;
          const newStock = Math.max(0, currentStock - item.quantity);
          const updatedStock = { ...product.stock, [item.warehouse_id]: newStock };
          await productsAPI.update(product.id, { ...product, stock: updatedStock });
        }
      }
      
      // Get unique warehouses used in this sale
      const usedWarehouses = [...new Set(quickSaleItems.map(item => item.warehouse_name))].join(', ');
      
      // Save to database
      const completedSale = {
        date: quickSaleDate,
        customer: quickSaleCustomer,
        description: quickSaleDescription,
        warehouse: quickSaleWarehouse,
        warehouseName: usedWarehouses || 'Belirtilmedi',
        items: [...quickSaleItems],
        total: quickSaleTotal,
        completedAt: new Date().toISOString()
      };
      
      // Save to MongoDB
      const savedSale = await quickSalesAPI.create(completedSale);
      setQuickSaleHistory(prev => [savedSale.data, ...prev]);
      
      // Reload data to reflect changes
      await loadData();
      
      alert('Satış başarıyla tamamlandı! Stoklar güncellendi.');
      
      // Clear form for new sale - keep warehouse selected for convenience
      setQuickSaleCustomer('');
      setQuickSaleDescription('');
      setQuickSaleItems([]);
      setQuickSaleSearch('');
      setQuickSaleDate(new Date().toISOString().split('T')[0]);
    } catch (error) {
      console.error('Quick sale error:', error);
      alert('Satış sırasında bir hata oluştu!');
    }
  };

  // Generate PDF for a specific sale (from history or current)
  const generateSalePDF = (sale) => {
    const pdfContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Hızlı Satış - ${sale.date}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
          .info { margin: 20px 0; }
          .info p { margin: 5px 0; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background-color: #1e40af; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .total { font-size: 18px; font-weight: bold; text-align: right; margin-top: 20px; }
          .footer { margin-top: 40px; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <h1>HIZLI SATIŞ FİŞİ</h1>
        <div class="info">
          <p><strong>Tarih:</strong> ${new Date(sale.date).toLocaleDateString('tr-TR')}</p>
          <p><strong>Müşteri:</strong> ${sale.customer || 'Belirtilmedi'}</p>
          <p><strong>Depolar:</strong> ${sale.warehouseName}</p>
          ${sale.description ? `<p><strong>Açıklama:</strong> ${sale.description}</p>` : ''}
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Ürün Kodu</th>
              <th>Ürün Adı</th>
              <th>Marka</th>
              <th>Depo</th>
              <th>Adet</th>
              <th>Birim Fiyat</th>
              <th>Toplam</th>
            </tr>
          </thead>
          <tbody>
            ${sale.items.map((item, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${item.product_code}</td>
                <td>${item.product_name}</td>
                <td>${item.brand}</td>
                <td>${item.warehouse_name || '-'}</td>
                <td>${item.quantity}</td>
                <td>${formatCurrency(item.unit_price)}</td>
                <td>${formatCurrency(item.quantity * item.unit_price)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="total">
          GENEL TOPLAM: ${formatCurrency(sale.total)}
        </div>
        <div class="footer">
          <p>Bu belge ${new Date().toLocaleString('tr-TR')} tarihinde oluşturulmuştur.</p>
          <p>Türkiye'nin Lastik Havuzu - B2B Sipariş Sistemi</p>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([pdfContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hizli-satis-${sale.date}-${sale.id}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateQuickSalePDF = () => {
    if (quickSaleItems.length === 0) {
      alert('PDF oluşturmak için en az bir ürün ekleyin!');
      return;
    }

    const warehouseName = warehouses.find(w => w.id === quickSaleWarehouse)?.name || 'Belirtilmedi';
    
    // Create PDF content as HTML
    const pdfContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Hızlı Satış - ${quickSaleDate}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
          .info { margin: 20px 0; }
          .info p { margin: 5px 0; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background-color: #1e40af; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .total { font-size: 18px; font-weight: bold; text-align: right; margin-top: 20px; }
          .footer { margin-top: 40px; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <h1>HIZLI SATIŞ FİŞİ</h1>
        <div class="info">
          <p><strong>Tarih:</strong> ${new Date(quickSaleDate).toLocaleDateString('tr-TR')}</p>
          <p><strong>Müşteri:</strong> ${quickSaleCustomer || 'Belirtilmedi'}</p>
          <p><strong>Depo:</strong> ${warehouseName}</p>
          ${quickSaleDescription ? `<p><strong>Açıklama:</strong> ${quickSaleDescription}</p>` : ''}
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Ürün Kodu</th>
              <th>Ürün Adı</th>
              <th>Marka</th>
              <th>Adet</th>
              <th>Birim Fiyat</th>
              <th>Toplam</th>
            </tr>
          </thead>
          <tbody>
            ${quickSaleItems.map((item, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${item.product_code}</td>
                <td>${item.product_name}</td>
                <td>${item.brand}</td>
                <td>${item.quantity}</td>
                <td>${formatCurrency(item.unit_price)}</td>
                <td>${formatCurrency(item.quantity * item.unit_price)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="total">
          GENEL TOPLAM: ${formatCurrency(quickSaleTotal)}
        </div>
        <div class="footer">
          <p>Bu belge ${new Date().toLocaleString('tr-TR')} tarihinde oluşturulmuştur.</p>
          <p>Türkiye'nin Lastik Havuzu - B2B Sipariş Sistemi</p>
        </div>
      </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([pdfContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hizli-satis-${quickSaleDate}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearQuickSale = () => {
    setQuickSaleCustomer('');
    setQuickSaleDescription('');
    setQuickSaleItems([]);
    setQuickSaleSearch('');
    setQuickSaleDate(new Date().toISOString().split('T')[0]);
  };

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const matchesSearch = !searchTerm || 
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.company?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = userFilter === 'all' || 
        (userFilter === 'pending' && u.status === 'pending') ||
        (userFilter === 'bireysel' && u.role === 'bireysel') ||
        (userFilter === 'toptanci' && u.role === 'toptanci');
      return matchesSearch && matchesFilter;
    });
  }, [users, searchTerm, userFilter]);

  // Filter orders
  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      const matchesSearch = !searchTerm || 
        o.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.user_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = orderFilter === 'all' || o.user_type === orderFilter;
      const matchesDate = !dateFilter || o.date?.startsWith(dateFilter);
      return matchesSearch && matchesFilter && matchesDate;
    });
  }, [orders, searchTerm, orderFilter, dateFilter]);

  // Dashboard stats
  const stats = {
    totalOrders: orders.length,
    pendingOrders: orders.filter(o => o.status === 'pending').length,
    totalRevenue: orders.reduce((sum, o) => sum + (o.total_amount || 0), 0),
    totalUsers: users.length,
    pendingUsers: users.filter(u => u.status === 'pending').length,
    totalProducts: products.length
  };

  // User actions
  const handleApproveUser = async (userId) => {
    try {
      await usersAPI.approve(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, status: 'active' } : u));
      alert('Kullanıcı onaylandı!');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Onaylama başarısız'));
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await usersAPI.reject(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, status: 'rejected' } : u));
      alert('Kullanıcı reddedildi!');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Reddetme başarısız'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) return;
    try {
      await usersAPI.delete(userId);
      setUsers(users.filter(u => u.id !== userId));
      alert('Kullanıcı silindi!');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Silme başarısız'));
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    
    // Şifre kontrolü
    if (editingUser.newPassword) {
      if (editingUser.newPassword !== editingUser.confirmPassword) {
        alert('Şifreler eşleşmiyor!');
        return;
      }
      if (editingUser.newPassword.length < 4) {
        alert('Şifre en az 4 karakter olmalıdır!');
        return;
      }
    }
    
    try {
      // Şifre değişikliği varsa ekle
      const updateData = { ...editingUser };
      if (editingUser.newPassword) {
        updateData.password = editingUser.newPassword;
      }
      // Geçici alanları temizle
      delete updateData.newPassword;
      delete updateData.confirmPassword;
      
      await usersAPI.update(editingUser.id, updateData);
      setUsers(users.map(u => u.id === editingUser.id ? { ...editingUser, newPassword: undefined, confirmPassword: undefined } : u));
      setEditingUser(null);
      alert(editingUser.newPassword ? 'Kullanıcı ve şifre güncellendi!' : 'Kullanıcı güncellendi!');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Güncelleme başarısız'));
    }
  };

  // Product actions
  const handleAddProduct = async () => {
    if (!newProduct.code || !newProduct.name || !newProduct.brand) {
      alert('Lütfen zorunlu alanları doldurun!');
      return;
    }
    try {
      const res = await productsAPI.create({
        ...newProduct,
        base_price: parseFloat(newProduct.base_price) || 0,
        iskonto1: parseFloat(newProduct.iskonto1) || 0,
        iskonto2: parseFloat(newProduct.iskonto2) || 0,
        iskonto3: parseFloat(newProduct.iskonto3) || 0,
        iskonto4: parseFloat(newProduct.iskonto4) || 0,
      });
      setProducts([...products, res.data]);
      setShowNewProductDialog(false);
      setNewProduct({ code: '', name: '', brand: '', dot: '2024', season: 'Yaz', base_price: '', iskonto1: 0, iskonto2: 0, iskonto3: 0, iskonto4: 0, image: '', stock: {} });
      alert('Ürün eklendi!');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Ürün eklenemedi'));
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;
    try {
      // Yeni alanlar için varsayılan değerler
      const productData = {
        ...editingProduct,
        visible_bireysel: editingProduct.visible_bireysel === true,
        visible_toptanci: editingProduct.visible_toptanci === true,
        depot_bireysel: editingProduct.depot_bireysel || 'all',
        depot_toptanci: editingProduct.depot_toptanci || 'all',
        shipping_cost: parseFloat(editingProduct.shipping_cost) || 0
      };
      
      await productsAPI.update(editingProduct.id, productData);
      setProducts(products.map(p => p.id === editingProduct.id ? productData : p));
      setEditingProduct(null);
      alert('Ürün güncellendi!');
    } catch (error) {
      console.error('Update error:', error);
      alert('Hata: ' + (error.response?.data?.detail || 'Güncelleme başarısız'));
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
    try {
      await productsAPI.delete(productId);
      setProducts(products.filter(p => p.id !== productId));
      alert('Ürün silindi!');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Silme başarısız'));
    }
  };

  const handleDeleteAllProducts = async () => {
    if (!window.confirm('TÜM ÜRÜNLERİ SİLMEK İSTEDİĞİNİZE EMİN MİSİNİZ? Bu işlem geri alınamaz!')) return;
    try {
      await productsAPI.deleteAll();
      setProducts([]);
      alert('Tüm ürünler silindi!');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Silme başarısız'));
    }
  };

  // Toplu görünürlük güncelleme
  const handleBulkVisibilityUpdate = async (field, value) => {
    if (selectedProducts.length === 0) {
      alert('Lütfen önce ürün seçin!');
      return;
    }
    
    const confirmMsg = `${selectedProducts.length} ürünün görünürlük ayarı güncellenecek. Devam etmek istiyor musunuz?`;
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const updatedProducts = [...products];
      for (const productId of selectedProducts) {
        const productIndex = updatedProducts.findIndex(p => p.id === productId);
        if (productIndex !== -1) {
          updatedProducts[productIndex] = {
            ...updatedProducts[productIndex],
            [field]: value
          };
          await productsAPI.update(productId, updatedProducts[productIndex]);
        }
      }
      setProducts(updatedProducts);
      setSelectedProducts([]);
      alert(`${selectedProducts.length} ürün güncellendi!`);
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Güncelleme başarısız'));
    }
  };

  // Tümünü seç/kaldır
  const handleSelectAllProducts = (checked) => {
    if (checked) {
      setSelectedProducts(filteredProductsForManagement.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  // Tek ürün seç/kaldır
  const handleSelectProduct = (productId, checked) => {
    if (checked) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  // Excel export
  const handleExportProducts = () => {
    let csv = '\ufeff';
    // Header - depo isimleri dinamik olarak ekleniyor
    let header = 'Ürün Kodu;Ürün Adı;Marka;DOT;Mevsim;Fiyat;İskonto1;İskonto2;İskonto3;İskonto4';
    warehouses.forEach(wh => {
      header += `;${wh.name} Stok`;
    });
    csv += header + '\n';
    
    // Data rows
    products.forEach(p => {
      let row = `${p.code};${p.name};${p.brand};${p.dot};${p.season};${p.base_price};${p.iskonto1 || 0};${p.iskonto2 || 0};${p.iskonto3 || 0};${p.iskonto4 || 0}`;
      warehouses.forEach(wh => {
        row += `;${p.stock?.[wh.id] || 0}`;
      });
      csv += row + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `urunler_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Excel import
  const handleImportProducts = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      
      if (lines.length < 2) {
        alert('Excel dosyası boş veya hatalı!');
        return;
      }

      // Parse header to get warehouse columns
      const headers = lines[0].split(';');
      const warehouseStartIndex = 10; // After İskonto4
      
      let imported = 0;
      let errors = 0;
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(';');
        if (values.length >= 6 && values[0]?.trim()) {
          try {
            // Build stock object from warehouse columns
            const stock = {};
            warehouses.forEach((wh, idx) => {
              const stockValue = parseInt(values[warehouseStartIndex + idx]) || 0;
              if (stockValue > 0) {
                stock[wh.id] = stockValue;
              }
            });

            const productData = {
              code: values[0]?.trim() || '',
              name: values[1]?.trim() || '',
              brand: values[2]?.trim() || '',
              dot: values[3]?.trim() || '2024',
              season: values[4]?.trim() || 'Yaz',
              base_price: parseFloat(values[5]) || 0,
              iskonto1: parseFloat(values[6]) || 0,
              iskonto2: parseFloat(values[7]) || 0,
              iskonto3: parseFloat(values[8]) || 0,
              iskonto4: parseFloat(values[9]) || 0,
              image: '',
              stock: stock
            };
            
            if (productData.code && productData.name) {
              await productsAPI.create(productData);
              imported++;
            }
          } catch (err) {
            console.error('Import error:', err);
            errors++;
          }
        }
      }
      
      alert(`${imported} ürün içe aktarıldı!${errors > 0 ? ` (${errors} hata)` : ''}`);
      loadData();
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Warehouse actions
  const handleAddWarehouse = async () => {
    if (!newWarehouse.trim()) return;
    try {
      const res = await warehousesAPI.create(newWarehouse);
      setWarehouses([...warehouses, res.data]);
      setNewWarehouse('');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Depo eklenemedi'));
    }
  };

  const handleDeleteWarehouse = async (id) => {
    try {
      await warehousesAPI.delete(id);
      setWarehouses(warehouses.filter(w => w.id !== id));
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Depo silinemedi'));
    }
  };

  // Bank account actions
  const handleAddBankAccount = async () => {
    if (!newBankAccount.bank_name || !newBankAccount.iban) return;
    try {
      const res = await bankAccountsAPI.create(newBankAccount);
      setBankAccounts([...bankAccounts, res.data]);
      setNewBankAccount({ bank_name: '', account_name: '', iban: '', branch: '', is_active: true });
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Hesap eklenemedi'));
    }
  };

  const handleDeleteBankAccount = async (id) => {
    try {
      await bankAccountsAPI.delete(id);
      setBankAccounts(bankAccounts.filter(b => b.id !== id));
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Hesap silinemedi'));
    }
  };

  const handleToggleBankAccount = async (id) => {
    const account = bankAccounts.find(b => b.id === id);
    if (!account) return;
    try {
      await bankAccountsAPI.update(id, { ...account, is_active: !account.is_active });
      setBankAccounts(bankAccounts.map(b => b.id === id ? { ...b, is_active: !b.is_active } : b));
    } catch (error) {
      alert('Hata: Güncelleme başarısız');
    }
  };

  // Credit card system actions
  const handleAddCreditCardSystem = async () => {
    if (!newCreditCardSystem.name) return;
    try {
      const res = await creditCardSystemsAPI.create(newCreditCardSystem);
      setCreditCardSystems([...creditCardSystems, res.data]);
      setNewCreditCardSystem({ name: '', api_key: '', secret_key: '', is_active: true });
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Sistem eklenemedi'));
    }
  };

  const handleDeleteCreditCardSystem = async (id) => {
    try {
      await creditCardSystemsAPI.delete(id);
      setCreditCardSystems(creditCardSystems.filter(c => c.id !== id));
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Sistem silinemedi'));
    }
  };

  const handleToggleCreditCardSystem = async (id) => {
    const system = creditCardSystems.find(c => c.id === id);
    if (!system) return;
    try {
      await creditCardSystemsAPI.update(id, { ...system, is_active: !system.is_active });
      setCreditCardSystems(creditCardSystems.map(c => c.id === id ? { ...c, is_active: !c.is_active } : c));
    } catch (error) {
      alert('Hata: Güncelleme başarısız');
    }
  };

  // Online payment save
  const handleSaveOnlinePayment = async () => {
    try {
      await onlinePaymentAPI.update(onlinePayment);
      alert('Ayarlar kaydedildi!');
    } catch (error) {
      alert('Hata: Kaydetme başarısız');
    }
  };

  // Discount settings save
  const handleSaveDiscountSettings = async () => {
    try {
      await discountSettingsAPI.update(discountSettings);
      alert('İskonto ayarları kaydedildi!');
    } catch (error) {
      alert('Hata: Kaydetme başarısız');
    }
  };

  // Admin settings save
  const handleSaveAdminSettings = async () => {
    if (adminSettings.newPassword && adminSettings.newPassword !== adminSettings.confirmPassword) {
      alert('Şifreler eşleşmiyor!');
      return;
    }
    try {
      await usersAPI.updateAdminSettings({
        username: adminSettings.username,
        new_password: adminSettings.newPassword || undefined
      });
      updateUser({ username: adminSettings.username });
      alert('Ayarlar kaydedildi!');
    } catch (error) {
      alert('Hata: Kaydetme başarısız');
    }
  };

  // Order status update
  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await ordersAPI.updateStatus(orderId, status);
      setOrders(orders.map(o => o.id === orderId ? { ...o, status } : o));
    } catch (error) {
      alert('Hata: Güncelleme başarısız');
    }
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'toplugorsel', label: 'Toplu Görsel', icon: Image },
    { id: 'genellistefiyatlari', label: 'Genel Liste Fiyatları', icon: FileSpreadsheet },
    { id: 'hizlisatis', label: 'Hızlı Satış', icon: ShoppingBag },
    { id: 'siparisler', label: 'Siparişler', icon: ClipboardList },
    { id: 'uyeler', label: 'Üye Yönetimi', icon: Users },
    { id: 'urunler', label: 'Ürün Yönetimi', icon: Package },
    { id: 'sayim', label: 'Sayım', icon: ClipboardList },
    { id: 'fiyatlama', label: 'Fiyatlama', icon: Percent },
    { id: 'depolar', label: 'Depo Yönetimi', icon: Building2 },
    { id: 'hesaplar', label: 'Hesap Numaraları', icon: Building2 },
    { id: 'onlineodeme', label: 'Online Ödeme', icon: ExternalLink },
    { id: 'kredikarti', label: 'Kredi Kartı Sistemleri', icon: CreditCard },
    { id: 'ayarlar', label: 'Ayarlar', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Navbar */}
      <header className="bg-slate-800 text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="lg:hidden p-2 rounded-md hover:bg-slate-700 mr-2">
                <Menu className="w-6 h-6" />
              </button>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                  <svg width="24" height="24" viewBox="0 0 100 100" className="text-white">
                    <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <circle cx="50" cy="50" r="25" stroke="currentColor" strokeWidth="3" fill="none"/>
                    <circle cx="50" cy="50" r="8" fill="currentColor"/>
                  </svg>
                </div>
                <div>
                  <h1 className="text-lg font-semibold">Türkiye&apos;nin Lastik Havuzu</h1>
                  <p className="text-xs text-slate-400">Admin Panel</p>
                </div>
              </div>
            </div>

            <div className="flex-1 max-w-xl mx-4 hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  placeholder="Ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={loadData} className="hover:bg-slate-700">
                <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
              <button className="relative p-2 hover:bg-slate-700 rounded-lg">
                <Bell className="w-5 h-5" />
                {stats.pendingUsers > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    {stats.pendingUsers}
                  </span>
                )}
              </button>
              <div className="hidden sm:flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-400">Admin</p>
                </div>
                <Button variant="ghost" size="icon" onClick={handleLogout} className="hover:bg-slate-700">
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-slate-800 text-white transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} pt-16 lg:pt-0`}>
          <div className="lg:hidden absolute top-4 right-4">
            <button onClick={() => setIsSidebarOpen(false)} className="p-2 rounded-md hover:bg-slate-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="p-4 space-y-1 lg:mt-4 overflow-y-auto max-h-[calc(100vh-5rem)]">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setIsSidebarOpen(false); }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all ${activeTab === item.id ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm">{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setIsSidebarOpen(false)} />}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-6 min-h-[calc(100vh-4rem)]">
          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Toplam Sipariş</p>
                        <p className="text-3xl font-bold">{stats.totalOrders}</p>
                      </div>
                      <ShoppingBag className="w-12 h-12 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Toplam Ciro</p>
                        <p className="text-3xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
                      </div>
                      <DollarSign className="w-12 h-12 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Toplam Üye</p>
                        <p className="text-3xl font-bold">{stats.totalUsers}</p>
                      </div>
                      <Users className="w-12 h-12 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Toplam Ürün</p>
                        <p className="text-3xl font-bold">{stats.totalProducts}</p>
                      </div>
                      <Package className="w-12 h-12 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {stats.pendingUsers > 0 && (
                <Card className="border-l-4 border-l-yellow-500">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <Bell className="w-5 h-5 mr-2 text-yellow-500" />
                      Onay Bekleyen Üyeler ({stats.pendingUsers})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableBody>
                        {users.filter(u => u.status === 'pending').slice(0, 5).map(u => (
                          <TableRow key={u.id}>
                            <TableCell>
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                  {u.role === 'toptanci' ? <Truck className="w-5 h-5 text-amber-600" /> : <User className="w-5 h-5 text-blue-600" />}
                                </div>
                                <div>
                                  <p className="font-medium">{u.name}</p>
                                  <p className="text-sm text-gray-500">{u.email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>{u.company}</TableCell>
                            <TableCell>
                              <Badge variant={u.role === 'toptanci' ? 'default' : 'secondary'}>
                                {u.role === 'toptanci' ? 'Toptancı' : 'Bireysel'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right space-x-2">
                              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApproveUser(u.id)}>
                                <CheckCircle className="w-4 h-4 mr-1" /> Onayla
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleRejectUser(u.id)}>
                                <XCircle className="w-4 h-4 mr-1" /> Reddet
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Toplu Görsel */}
          {activeTab === 'toplugorsel' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Toplu Görsel Ekleme</h2>
                <Badge variant="outline" className="text-sm">
                  {productNameGroups.length} Grup • {products.length} Ürün
                </Badge>
              </div>

              {/* Açıklama */}
              <Alert className="bg-blue-50 border-blue-200">
                <Image className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-blue-800 ml-2">
                  Ürünler benzer isimlerine göre otomatik gruplandı. Bir gruba görsel linki yapıştırarak 
                  o gruptaki tüm ürünlere aynı anda görsel ekleyebilirsiniz.
                </AlertDescription>
              </Alert>

              {/* TÜM Ürünler İçin Toplu Görünürlük Butonları */}
              <Card className="border-2 border-orange-200 bg-orange-50">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-orange-600" />
                      <span className="font-semibold text-orange-800">Kullanıcı Paneli Görsel Ayarları:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {/* Bireysel Görsel Ayarı */}
                      <div className="flex items-center gap-1 bg-white rounded-lg px-3 py-1 border">
                        <span className="text-sm text-gray-600 mr-2">Bireysel:</span>
                        <Button 
                          size="sm"
                          className={`h-7 px-3 ${showImagesBireysel ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 hover:bg-gray-500'} text-white`}
                          onClick={() => toggleShowImagesBireysel(true)}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Göster
                        </Button>
                        <Button 
                          size="sm"
                          className={`h-7 px-3 ${!showImagesBireysel ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-400 hover:bg-gray-500'} text-white`}
                          onClick={() => toggleShowImagesBireysel(false)}
                        >
                          <XCircle className="w-3 h-3 mr-1" />
                          Gösterme
                        </Button>
                      </div>
                      {/* Toptancı Görsel Ayarı */}
                      <div className="flex items-center gap-1 bg-white rounded-lg px-3 py-1 border">
                        <span className="text-sm text-gray-600 mr-2">Toptancı:</span>
                        <Button 
                          size="sm"
                          className={`h-7 px-3 ${showImagesToptanci ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 hover:bg-gray-500'} text-white`}
                          onClick={() => toggleShowImagesToptanci(true)}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Göster
                        </Button>
                        <Button 
                          size="sm"
                          className={`h-7 px-3 ${!showImagesToptanci ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-400 hover:bg-gray-500'} text-white`}
                          onClick={() => toggleShowImagesToptanci(false)}
                        >
                          <XCircle className="w-3 h-3 mr-1" />
                          Gösterme
                        </Button>
                      </div>
                    </div>
                    <span className="text-xs text-orange-600">
                      {showImagesBireysel ? '✓ Bireysel görseller açık' : '✗ Bireysel görseller kapalı'} | 
                      {showImagesToptanci ? ' ✓ Toptancı görseller açık' : ' ✗ Toptancı görseller kapalı'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Ürün Yönetimi Resim Sütunu Ayarı */}
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Image className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-purple-800">Ürün Yönetimi - Resim Sütunu:</span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm"
                        className={`h-8 px-4 ${showProductImages ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-400 hover:bg-gray-500'}`}
                        onClick={() => toggleShowProductImages(true)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Göster
                      </Button>
                      <Button 
                        size="sm"
                        className={`h-8 px-4 ${!showProductImages ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-400 hover:bg-gray-500'}`}
                        onClick={() => toggleShowProductImages(false)}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Gösterme
                      </Button>
                    </div>
                    <span className="text-xs text-purple-600">
                      {showProductImages ? '✓ Resimler görünüyor' : '✗ Resimler gizli'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Arama */}
              <Card>
                <CardContent className="p-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      placeholder="Grup ara (ürün adı, kod, marka)..."
                      value={bulkImageSearch}
                      onChange={(e) => setBulkImageSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Grup Listesi */}
              {filteredProductGroups.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">
                      {bulkImageSearch ? 'Arama kriterine uygun grup bulunamadı.' : 'Henüz ürün yok.'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filteredProductGroups.map((group) => (
                    <Card key={group.baseName} className="overflow-hidden">
                      <div 
                        className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 flex items-center justify-between"
                        onClick={() => setBulkImageExpandedGroup(bulkImageExpandedGroup === group.baseName ? null : group.baseName)}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${bulkImageExpandedGroup === group.baseName ? 'rotate-180' : ''}`} />
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm sm:text-base font-semibold truncate">{group.displayName}</h3>
                            <p className="text-xs text-gray-500">{group.brands || '-'}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge>{group.products.length} ürün</Badge>
                            {group.hasImage ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                <CheckCircle className="w-3 h-3 mr-1" /> Görsel Var
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                <XCircle className="w-3 h-3 mr-1" /> Görsel Yok
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {bulkImageExpandedGroup === group.baseName && (
                        <CardContent className="p-4 border-t space-y-4">
                          {/* Görsel Ekleme Formu */}
                          <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                            <Label className="text-sm font-medium">Bu gruptaki tüm ürünlere görsel ekle:</Label>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <Input
                                placeholder="Görsel URL'si yapıştırın (örn: https://example.com/image.jpg)"
                                value={bulkImageUrl}
                                onChange={(e) => setBulkImageUrl(e.target.value)}
                                className="flex-1"
                              />
                              <Button 
                                onClick={() => applyBulkImage(group.baseName, bulkImageUrl)}
                                disabled={bulkImageLoading || !bulkImageUrl.trim()}
                                className="whitespace-nowrap"
                              >
                                {bulkImageLoading ? (
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Image className="w-4 h-4 mr-2" />
                                )}
                                {group.products.length} Ürüne Uygula
                              </Button>
                            </div>
                          </div>

                          {/* Görünürlük Ayarları */}
                          <div className="bg-orange-50 rounded-lg p-4 space-y-3">
                            <Label className="text-sm font-medium">Toplu Görünürlük Ayarı:</Label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {/* Bireysel Görünürlük */}
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 min-w-[80px]">Bireysel:</span>
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                                  onClick={() => applyBulkVisibility(group.baseName, 'visible_bireysel', true)}
                                  disabled={bulkImageLoading}
                                >
                                  <Eye className="w-3 h-3 mr-1" /> Göster
                                </Button>
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  className="bg-red-50 hover:bg-red-100 text-red-700 border-red-300"
                                  onClick={() => applyBulkVisibility(group.baseName, 'visible_bireysel', false)}
                                  disabled={bulkImageLoading}
                                >
                                  <XCircle className="w-3 h-3 mr-1" /> Gösterme
                                </Button>
                              </div>
                              {/* Toptancı Görünürlük */}
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 min-w-[80px]">Toptancı:</span>
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                                  onClick={() => applyBulkVisibility(group.baseName, 'visible_toptanci', true)}
                                  disabled={bulkImageLoading}
                                >
                                  <Eye className="w-3 h-3 mr-1" /> Göster
                                </Button>
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  className="bg-red-50 hover:bg-red-100 text-red-700 border-red-300"
                                  onClick={() => applyBulkVisibility(group.baseName, 'visible_toptanci', false)}
                                  disabled={bulkImageLoading}
                                >
                                  <XCircle className="w-3 h-3 mr-1" /> Gösterme
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Mevcut Görsel Önizleme */}
                          {group.products[0]?.image && (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <Label className="text-sm font-medium mb-2 block">Mevcut Görsel:</Label>
                              <div className="flex items-center gap-4">
                                <img 
                                  src={group.products[0].image} 
                                  alt="Ürün görseli" 
                                  className="w-24 h-24 object-contain rounded border bg-white"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <p className="text-xs text-gray-500 break-all flex-1">{group.products[0].image}</p>
                              </div>
                            </div>
                          )}

                          {/* Gruptaki Ürünler */}
                          <div>
                            <Label className="text-sm font-medium mb-2 block">Gruptaki Ürünler ({group.products.length}):</Label>
                            <ScrollArea className="h-48 border rounded-lg">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="w-16">Görsel</TableHead>
                                    <TableHead>Kod</TableHead>
                                    <TableHead>Ürün Adı</TableHead>
                                    <TableHead>Marka</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {group.products.map(product => (
                                    <TableRow key={product.id}>
                                      <TableCell>
                                        {product.image ? (
                                          <img 
                                            src={product.image} 
                                            alt="" 
                                            className="w-10 h-10 object-contain rounded"
                                            onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect fill="%23eee" width="40" height="40"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-size="8">Yok</text></svg>'; }}
                                          />
                                        ) : (
                                          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                                            <XCircle className="w-4 h-4 text-gray-400" />
                                          </div>
                                        )}
                                      </TableCell>
                                      <TableCell className="font-mono text-xs">{product.code}</TableCell>
                                      <TableCell className="text-sm">{product.name}</TableCell>
                                      <TableCell className="text-sm">{product.brand}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </ScrollArea>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Genel Liste Fiyatları */}
          {activeTab === 'genellistefiyatlari' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Genel Liste Fiyatları</h2>
                <Badge variant="outline" className="text-sm">
                  {priceList.length} Kayıt
                </Badge>
              </div>

              {/* Açıklama ve Butonlar */}
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-blue-800">
                        Excel sütunları: <strong>Stok Kodu | Ürün Adı | Liste Fiyatı | Perakende Fiyatı | Toptan Fiyatı | Jant Grubu</strong>
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {/* Excel Yükle */}
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handlePriceListUpload}
                        ref={priceListFileInputRef}
                        className="hidden"
                        id="price-list-upload"
                      />
                      <Button 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => priceListFileInputRef.current?.click()}
                        disabled={priceListLoading}
                      >
                        {priceListLoading ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Excel Yükle
                      </Button>
                      
                      {/* Excel İndir */}
                      <Button 
                        variant="outline"
                        onClick={handlePriceListDownload}
                        disabled={priceList.length === 0 || priceListLoading}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Excel İndir
                      </Button>
                      
                      {/* Tümünü Sil */}
                      <Button 
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={handlePriceListDelete}
                        disabled={priceList.length === 0 || priceListLoading}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Tümünü Sil
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Arama */}
              <Card>
                <CardContent className="p-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      placeholder="Ürün ara (stok kodu, ürün adı, jant grubu, 2055516 gibi)..."
                      value={priceListSearch}
                      onChange={(e) => setPriceListSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {priceListSearch && (
                    <p className="text-sm text-gray-500 mt-2">
                      {filteredPriceList.length} / {priceList.length} kayıt gösteriliyor
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Veri Tablosu */}
              {priceList.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 mb-4">Henüz liste fiyatı yüklenmemiş.</p>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => priceListFileInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      İlk Excel Dosyanızı Yükleyin
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[600px]">
                      <Table>
                        <TableHeader className="sticky top-0 bg-gray-100 z-10">
                          <TableRow>
                            <TableHead className="font-bold">Stok Kodu</TableHead>
                            <TableHead className="font-bold">Ürün Adı</TableHead>
                            <TableHead className="font-bold text-right">Liste Fiyatı</TableHead>
                            <TableHead className="font-bold text-right">Perakende Fiyatı</TableHead>
                            <TableHead className="font-bold text-right">Toptan Fiyatı</TableHead>
                            <TableHead className="font-bold">Jant Grubu</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredPriceList.map((item, index) => (
                            <TableRow key={index} className="hover:bg-gray-50">
                              <TableCell className="font-mono text-sm">{item.stok_kodu}</TableCell>
                              <TableCell>{item.urun_adi}</TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(item.liste_fiyati)}</TableCell>
                              <TableCell className="text-right font-medium text-blue-600">{formatCurrency(item.perakende_fiyati)}</TableCell>
                              <TableCell className="text-right font-medium text-green-600">{formatCurrency(item.toptan_fiyati)}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{item.jant_grubu}</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Hızlı Satış */}
          {activeTab === 'hizlisatis' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Hızlı Satış</h2>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={clearQuickSale}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Temizle
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={generateQuickSalePDF}
                    disabled={quickSaleItems.length === 0}
                  >
                    <Download className="w-4 h-4 mr-2" /> PDF İndir
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Sol Panel - Satış Bilgileri */}
                <Card className="lg:col-span-1">
                  <CardHeader>
                    <CardTitle className="text-lg">Satış Bilgileri</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Tarih</Label>
                      <Input 
                        type="date" 
                        value={quickSaleDate} 
                        onChange={(e) => setQuickSaleDate(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Depo Seçimi *</Label>
                      <Select value={quickSaleWarehouse} onValueChange={setQuickSaleWarehouse}>
                        <SelectTrigger>
                          <SelectValue placeholder="Depo seçin..." />
                        </SelectTrigger>
                        <SelectContent>
                          {warehouses.map(wh => (
                            <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Müşteri Adı Soyadı</Label>
                      <Input 
                        value={quickSaleCustomer} 
                        onChange={(e) => setQuickSaleCustomer(e.target.value)}
                        placeholder="Müşteri adı..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Açıklama</Label>
                      <Textarea 
                        value={quickSaleDescription} 
                        onChange={(e) => setQuickSaleDescription(e.target.value)}
                        placeholder="Satış notu..."
                        rows={3}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Sağ Panel - Ürün Ekleme ve Liste */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg">Ürünler</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Ürün Arama */}
                    <div className="relative">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <Input 
                            value={quickSaleSearch}
                            onChange={(e) => {
                              setQuickSaleSearch(e.target.value);
                              setQuickSaleShowProducts(e.target.value.length > 0);
                            }}
                            onFocus={() => quickSaleSearch && setQuickSaleShowProducts(true)}
                            placeholder="Ürün ara... (205/55, michelin, primacy vb.)"
                            className="pl-10"
                          />
                        </div>
                      </div>
                      
                      {/* Ürün Dropdown */}
                      {quickSaleShowProducts && quickSaleFilteredProducts.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                          {quickSaleFilteredProducts.map(product => {
                            const stock = quickSaleWarehouse 
                              ? (product.stock?.[quickSaleWarehouse] || 0)
                              : Object.values(product.stock || {}).reduce((a, b) => a + b, 0);
                            return (
                              <div 
                                key={product.id}
                                className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
                                onClick={() => addQuickSaleItem(product)}
                              >
                                <div>
                                  <p className="font-medium text-sm">{product.name}</p>
                                  <p className="text-xs text-gray-500">{product.code} - {product.brand}</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold text-sm">{formatCurrency(product.base_price)}</p>
                                  <Badge variant={stock > 0 ? 'default' : 'secondary'} className="text-xs">
                                    {stock} adet
                                  </Badge>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {!quickSaleWarehouse && (
                      <Alert className="bg-yellow-50 border-yellow-200">
                        <AlertDescription className="text-yellow-800">
                          Ürün eklemek için önce bir depo seçin.
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Eklenen Ürünler Listesi */}
                    {quickSaleItems.length > 0 ? (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Ürün</TableHead>
                              <TableHead className="w-24">Depo</TableHead>
                              <TableHead className="w-20">Adet</TableHead>
                              <TableHead className="w-28">Fiyat (₺)</TableHead>
                              <TableHead className="w-28">Toplam</TableHead>
                              <TableHead className="w-12"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {quickSaleItems.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <div>
                                    <p className="font-medium text-sm">{item.product_name}</p>
                                    <p className="text-xs text-gray-500">{item.product_code}</p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-xs">
                                    {item.warehouse_name}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Input 
                                    type="number" 
                                    min="1"
                                    max={item.available_stock}
                                    value={item.quantity}
                                    onChange={(e) => updateQuickSaleItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                    className="w-16 text-center"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input 
                                    type="number" 
                                    min="0"
                                    step="0.01"
                                    value={item.unit_price}
                                    onChange={(e) => updateQuickSaleItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                    className="w-24"
                                  />
                                </TableCell>
                                <TableCell className="font-semibold">
                                  {formatCurrency(item.quantity * item.unit_price)}
                                </TableCell>
                                <TableCell>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="text-red-500 hover:text-red-700"
                                    onClick={() => removeQuickSaleItem(index)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>Henüz ürün eklenmedi</p>
                        <p className="text-sm">Yukarıdan ürün arayarak ekleyin</p>
                      </div>
                    )}

                    {/* Toplam ve Satış Butonu */}
                    {quickSaleItems.length > 0 && (
                      <div className="border-t pt-4 space-y-4">
                        <div className="flex justify-between items-center text-lg">
                          <span className="font-medium">Toplam ({quickSaleItems.length} ürün):</span>
                          <span className="font-bold text-xl text-blue-600">{formatCurrency(quickSaleTotal)}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                            onClick={handleQuickSale}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" /> Satışı Tamamla
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={generateQuickSalePDF}
                          >
                            <Download className="w-4 h-4 mr-2" /> PDF
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Satış Geçmişi */}
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ClipboardList className="w-5 h-5" />
                      Satış Geçmişi
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-2">
                      <Label className="whitespace-nowrap">Başlangıç:</Label>
                      <Input 
                        type="date" 
                        value={quickSaleHistoryStartDate} 
                        onChange={(e) => setQuickSaleHistoryStartDate(e.target.value)}
                        className="w-40"
                      />
                      <Label className="whitespace-nowrap">Bitiş:</Label>
                      <Input 
                        type="date" 
                        value={quickSaleHistoryEndDate} 
                        onChange={(e) => setQuickSaleHistoryEndDate(e.target.value)}
                        className="w-40"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const filteredSales = quickSaleHistory.filter(sale => {
                      const saleDate = sale.date;
                      return saleDate >= quickSaleHistoryStartDate && saleDate <= quickSaleHistoryEndDate;
                    });
                    const totalAmount = filteredSales.reduce((sum, sale) => sum + (sale.total || 0), 0);
                    const totalItems = filteredSales.reduce((sum, sale) => sum + (sale.items?.reduce((s, i) => s + i.quantity, 0) || 0), 0);
                    
                    if (filteredSales.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500">
                          <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>Bu tarih aralığında satış bulunamadı</p>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-4">
                        {/* Özet Bilgiler */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Toplam Satış</p>
                            <p className="text-2xl font-bold text-blue-600">{filteredSales.length}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Toplam Adet</p>
                            <p className="text-2xl font-bold text-green-600">{totalItems}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Toplam Tutar</p>
                            <p className="text-2xl font-bold text-purple-600">{formatCurrency(totalAmount)}</p>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-2">
                          <Badge variant="outline">{filteredSales.length}</Badge> satış bulundu
                        </div>
                        {filteredSales.map((sale, index) => (
                          <div key={sale.id} className="border rounded-lg p-4 bg-gray-50">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">#{filteredSales.length - index}</Badge>
                                  <span className="font-semibold">{sale.customer || 'İsimsiz Müşteri'}</span>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                  {new Date(sale.completedAt).toLocaleString('tr-TR')} • {sale.warehouseName}
                                  {sale.description && ` • ${sale.description}`}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-blue-600">{formatCurrency(sale.total)}</span>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => generateSalePDF(sale)}
                                >
                                  <Download className="w-4 h-4 mr-1" /> PDF
                                </Button>
                              </div>
                            </div>
                            <div className="border-t pt-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                {sale.items.map((item, itemIndex) => (
                                  <div key={itemIndex} className="bg-white p-2 rounded border text-sm">
                                    <p className="font-medium truncate">{item.product_name}</p>
                                    <p className="text-gray-500 text-xs">{item.product_code}</p>
                                    <div className="flex justify-between mt-1">
                                      <span>{item.quantity} adet</span>
                                    <span className="font-semibold">{formatCurrency(item.quantity * item.unit_price)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Siparişler */}
          {activeTab === 'siparisler' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-800">Siparişler</h2>
                <div className="flex flex-wrap gap-2">
                  <Select value={orderFilter} onValueChange={setOrderFilter}>
                    <SelectTrigger className="w-40"><SelectValue placeholder="Müşteri Tipi" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="bireysel">Bireysel</SelectItem>
                      <SelectItem value="toptanci">Toptancı</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-auto" />
                </div>
              </div>
              <Card>
                <CardContent className="p-0">
                  <ScrollArea className="w-full">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Sipariş No</TableHead>
                          <TableHead>Müşteri</TableHead>
                          <TableHead>Tip</TableHead>
                          <TableHead>Tutar</TableHead>
                          <TableHead>Tarih</TableHead>
                          <TableHead>Durum</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredOrders.map(order => (
                          <TableRow key={order.id}>
                            <TableCell className="font-medium">{order.id}</TableCell>
                            <TableCell>{order.user_name}</TableCell>
                            <TableCell>
                              <Badge variant={order.user_type === 'toptanci' ? 'default' : 'secondary'}>
                                {order.user_type === 'toptanci' ? 'Toptancı' : 'Bireysel'}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatCurrency(order.total_amount)}</TableCell>
                            <TableCell>{order.date ? new Date(order.date).toLocaleDateString('tr-TR') : '-'}</TableCell>
                            <TableCell>
                              <Select defaultValue={order.status} onValueChange={(v) => handleUpdateOrderStatus(order.id, v)}>
                                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Beklemede</SelectItem>
                                  <SelectItem value="processing">Hazırlanıyor</SelectItem>
                                  <SelectItem value="shipped">Kargoda</SelectItem>
                                  <SelectItem value="delivered">Teslim Edildi</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl">
                                  <DialogHeader><DialogTitle>Sipariş Detayı - {order.id}</DialogTitle></DialogHeader>
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Ürün</TableHead>
                                        <TableHead>Depo</TableHead>
                                        <TableHead>Adet</TableHead>
                                        <TableHead>Birim Fiyat</TableHead>
                                        <TableHead>Toplam</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {order.items?.map((item, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell>{item.product_name}</TableCell>
                                          <TableCell><Badge variant="outline">{item.warehouse_name || 'Depo'}</Badge></TableCell>
                                          <TableCell>{item.quantity}</TableCell>
                                          <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                                          <TableCell>{formatCurrency(item.total)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                  <div className="text-right border-t pt-4">
                                    <p className="text-xl font-bold">Toplam: {formatCurrency(order.total_amount)}</p>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                  {filteredOrders.length === 0 && (
                    <div className="text-center py-12"><ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-3" /><p className="text-gray-500">Sipariş bulunamadı.</p></div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Üye Yönetimi */}
          {activeTab === 'uyeler' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-800">Üye Yönetimi</h2>
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger className="w-40"><SelectValue placeholder="Filtrele" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="pending">Onay Bekleyen</SelectItem>
                    <SelectItem value="bireysel">Bireysel</SelectItem>
                    <SelectItem value="toptanci">Toptancı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <Table className="min-w-[900px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Üye Bilgileri</TableHead>
                        <TableHead>İletişim</TableHead>
                        <TableHead>Firma Bilgileri</TableHead>
                        <TableHead>Tip</TableHead>
                        <TableHead>Kayıt Tarihi</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map(u => {
                        const isExpanded = expandedUserId === u.id;
                        return (
                          <React.Fragment key={u.id}>
                            <TableRow 
                              className="cursor-pointer hover:bg-gray-50" 
                              onClick={() => setExpandedUserId(isExpanded ? null : u.id)}
                            >
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${isExpanded ? 'bg-blue-500' : 'bg-gray-300'}`} />
                                  <div>
                                    <p className="font-medium">{u.name}</p>
                                    <p className="text-sm text-gray-500">{u.email}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <p className="text-sm">{u.phone || '-'}</p>
                              </TableCell>
                              <TableCell>
                                <p className="font-medium text-sm">{u.company || '-'}</p>
                              </TableCell>
                              <TableCell>
                                <Badge variant={u.role === 'toptanci' ? 'default' : 'secondary'}>
                                  {u.role === 'toptanci' ? 'Toptancı' : 'Bireysel'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <p className="text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString('tr-TR') : '-'}</p>
                              </TableCell>
                              <TableCell>
                                <Badge className={u.status === 'active' ? 'bg-green-100 text-green-800' : u.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                                  {u.status === 'active' ? 'Aktif' : u.status === 'rejected' ? 'Reddedildi' : 'Onay Bekliyor'}
                                </Badge>
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <div className="flex gap-1">
                                  {u.status === 'pending' && (
                                    <>
                                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApproveUser(u.id)}>
                                        <CheckCircle className="w-4 h-4" />
                                      </Button>
                                      <Button size="sm" variant="destructive" onClick={() => handleRejectUser(u.id)}>
                                        <XCircle className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                  <Dialog>
                                    <DialogTrigger asChild>
                                      <Button size="sm" variant="outline" onClick={() => setEditingUser({ ...u })}>
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                      <DialogHeader><DialogTitle>Üye Düzenle</DialogTitle></DialogHeader>
                                      {editingUser && (
                                        <div className="space-y-4 py-4">
                                          <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                              <Label>Ad Soyad</Label>
                                              <Input value={editingUser.name || ''} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} />
                                            </div>
                                            <div className="space-y-2">
                                              <Label>E-posta</Label>
                                              <Input value={editingUser.email || ''} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} />
                                            </div>
                                            <div className="space-y-2">
                                              <Label>Telefon</Label>
                                              <Input value={editingUser.phone || ''} onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })} />
                                            </div>
                                            <div className="space-y-2">
                                              <Label>Firma</Label>
                                              <Input value={editingUser.company || ''} onChange={(e) => setEditingUser({ ...editingUser, company: e.target.value })} />
                                            </div>
                                            <div className="space-y-2">
                                              <Label>Üye Tipi</Label>
                                              <Select value={editingUser.role} onValueChange={(v) => setEditingUser({ ...editingUser, role: v })}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="bireysel">Bireysel</SelectItem>
                                                  <SelectItem value="toptanci">Toptancı</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            </div>
                                            <div className="space-y-2">
                                              <Label>Durum</Label>
                                              <Select value={editingUser.status} onValueChange={(v) => setEditingUser({ ...editingUser, status: v })}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="active">Aktif</SelectItem>
                                                  <SelectItem value="pending">Onay Bekliyor</SelectItem>
                                                  <SelectItem value="rejected">Reddedildi</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          </div>
                                          {/* Şifre Değiştirme Bölümü */}
                                          <div className="border-t pt-4 mt-4">
                                            <Label className="text-orange-600 font-semibold">Şifre Yönetimi</Label>
                                            <p className="text-xs text-gray-500 mb-2">Yeni şifre girerek üyenin şifresini değiştirebilirsiniz. Boş bırakırsanız şifre değişmez.</p>
                                            <div className="grid grid-cols-2 gap-4">
                                              <div className="space-y-2">
                                                <Label>Yeni Şifre</Label>
                                                <Input 
                                                  type="text" 
                                                  value={editingUser.newPassword || ''} 
                                                  onChange={(e) => setEditingUser({ ...editingUser, newPassword: e.target.value })}
                                                  placeholder="Yeni şifre girin..."
                                                />
                                              </div>
                                              <div className="space-y-2">
                                                <Label>Şifre Tekrar</Label>
                                                <Input 
                                                  type="text" 
                                                  value={editingUser.confirmPassword || ''} 
                                                  onChange={(e) => setEditingUser({ ...editingUser, confirmPassword: e.target.value })}
                                                  placeholder="Şifreyi tekrar girin..."
                                                />
                                              </div>
                                            </div>
                                            {editingUser.newPassword && editingUser.confirmPassword && editingUser.newPassword !== editingUser.confirmPassword && (
                                              <p className="text-red-500 text-sm mt-2">Şifreler eşleşmiyor!</p>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      <DialogFooter>
                                        <Button variant="outline" onClick={() => setEditingUser(null)}>İptal</Button>
                                        <Button onClick={handleUpdateUser}>Kaydet</Button>
                                      </DialogFooter>
                                    </DialogContent>
                                  </Dialog>
                                  <Button size="sm" variant="destructive" onClick={() => handleDeleteUser(u.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                            {/* Detay Paneli - Açılır Çekmece */}
                            {isExpanded && (
                              <TableRow className="bg-blue-50">
                                <TableCell colSpan={7} className="p-4">
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {/* Kişisel Bilgiler */}
                                    <div className="bg-white p-4 rounded-lg shadow-sm">
                                      <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                        <User className="w-4 h-4" /> Kişisel Bilgiler
                                      </h4>
                                      <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Ad Soyad:</span>
                                          <span className="font-medium">{u.name || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Kullanıcı Adı:</span>
                                          <span className="font-medium">{u.username || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">E-posta:</span>
                                          <span className="font-medium">{u.email || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Telefon:</span>
                                          <span className="font-medium">{u.phone || '-'}</span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Firma Bilgileri */}
                                    <div className="bg-white p-4 rounded-lg shadow-sm">
                                      <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                        <Building2 className="w-4 h-4" /> Firma Bilgileri
                                      </h4>
                                      <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Firma Adı:</span>
                                          <span className="font-medium">{u.company || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Vergi Dairesi:</span>
                                          <span className="font-medium">{u.tax_office || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Vergi No:</span>
                                          <span className="font-medium">{u.tax_number || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Üye Tipi:</span>
                                          <Badge variant={u.role === 'toptanci' ? 'default' : 'secondary'} className="text-xs">
                                            {u.role === 'toptanci' ? 'Toptancı' : 'Bireysel'}
                                          </Badge>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Adres ve Diğer Bilgiler */}
                                    <div className="bg-white p-4 rounded-lg shadow-sm">
                                      <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                        <ClipboardList className="w-4 h-4" /> Adres ve Diğer
                                      </h4>
                                      <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Şehir:</span>
                                          <span className="font-medium">{u.city || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">İlçe:</span>
                                          <span className="font-medium">{u.district || '-'}</span>
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Adres:</span>
                                          <p className="font-medium mt-1">{u.address || '-'}</p>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">Kayıt Tarihi:</span>
                                          <span className="font-medium">{u.created_at ? new Date(u.created_at).toLocaleString('tr-TR') : '-'}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {filteredUsers.length === 0 && (
                    <div className="text-center py-12"><Users className="w-12 h-12 mx-auto text-gray-300 mb-3" /><p className="text-gray-500">Üye bulunamadı.</p></div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Ürün Yönetimi */}
          {activeTab === 'urunler' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Ürün Yönetimi ({products.length})</h2>
                <div className="flex flex-wrap gap-2">
                  <Dialog open={showNewProductDialog} onOpenChange={setShowNewProductDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-blue-600 hover:bg-blue-700 text-sm"><Plus className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Yeni</span> Ürün</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>Yeni Ürün Ekle</DialogTitle></DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Ürün Kodu *</Label>
                            <Input value={newProduct.code} onChange={(e) => setNewProduct({ ...newProduct, code: e.target.value })} placeholder="MCH-205-55-R16" />
                          </div>
                          <div className="space-y-2">
                            <Label>Marka *</Label>
                            <Input value={newProduct.brand} onChange={(e) => setNewProduct({ ...newProduct, brand: e.target.value })} placeholder="Michelin" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Ürün Adı *</Label>
                          <Input value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} placeholder="Primacy 4+ 205/55 R16 91V" />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>DOT</Label>
                            <Input value={newProduct.dot} onChange={(e) => setNewProduct({ ...newProduct, dot: e.target.value })} />
                          </div>
                          <div className="space-y-2">
                            <Label>Mevsim</Label>
                            <Select value={newProduct.season} onValueChange={(v) => setNewProduct({ ...newProduct, season: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Yaz">Yaz</SelectItem>
                                <SelectItem value="Kış">Kış</SelectItem>
                                <SelectItem value="4 Mevsim">4 Mevsim</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2 col-span-2 sm:col-span-1">
                            <Label>Ana Fiyat (₺)</Label>
                            <Input type="number" value={newProduct.base_price} onChange={(e) => setNewProduct({ ...newProduct, base_price: e.target.value })} />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Ürün Resmi URL</Label>
                          <Input value={newProduct.image} onChange={(e) => setNewProduct({ ...newProduct, image: e.target.value })} placeholder="https://..." />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          <div className="space-y-2"><Label>İskonto 1 (%)</Label><Input type="number" value={newProduct.iskonto1} onChange={(e) => setNewProduct({ ...newProduct, iskonto1: e.target.value })} /></div>
                          <div className="space-y-2"><Label>İskonto 2 (%)</Label><Input type="number" value={newProduct.iskonto2} onChange={(e) => setNewProduct({ ...newProduct, iskonto2: e.target.value })} /></div>
                          <div className="space-y-2"><Label>İskonto 3 (%)</Label><Input type="number" value={newProduct.iskonto3} onChange={(e) => setNewProduct({ ...newProduct, iskonto3: e.target.value })} /></div>
                          <div className="space-y-2"><Label>İskonto 4 (%)</Label><Input type="number" value={newProduct.iskonto4} onChange={(e) => setNewProduct({ ...newProduct, iskonto4: e.target.value })} /></div>
                        </div>
                        <div className="space-y-2">
                          <Label>Depo Stokları</Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {warehouses.map(wh => (
                              <div key={wh.id} className="flex items-center gap-2">
                                <Label className="w-24 text-sm">{wh.name}:</Label>
                                <Input type="number" className="w-20" placeholder="0" onChange={(e) => setNewProduct({ ...newProduct, stock: { ...newProduct.stock, [wh.id]: parseInt(e.target.value) || 0 } })} />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={() => setShowNewProductDialog(false)}>İptal</Button>
                        <Button onClick={handleAddProduct}>Ürün Ekle</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <label className="cursor-pointer">
                    <input type="file" accept=".csv,.txt" className="hidden" onChange={handleImportProducts} />
                    <Button className="bg-green-600 hover:bg-green-700 text-sm" asChild><span><Upload className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Excel</span> Yükle</span></Button>
                  </label>
                  <Button variant="outline" onClick={handleExportProducts} className="text-sm"><Download className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Excel</span> İndir</Button>
                  <Button variant="destructive" onClick={handleDeleteAllProducts} className="text-sm"><Trash2 className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Tümünü</span> Sil</Button>
                </div>
              </div>

              {/* Depo Filtresi ve Ürün Arama */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  {/* Akıllı Ürün Arama */}
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input 
                      value={productSearchTerm}
                      onChange={(e) => setProductSearchTerm(e.target.value)}
                      placeholder="Ürün ara... (205/55, michelin, lassa kamyon vb.)"
                      className="pl-10"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="whitespace-nowrap">Depo:</Label>
                    <Select value={productWarehouseFilter} onValueChange={setProductWarehouseFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Tüm Depolar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tüm Depolar</SelectItem>
                        {warehouses.map(wh => (
                          <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="whitespace-nowrap">Stok:</Label>
                    <Select value={productStockFilter} onValueChange={setProductStockFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Tüm Stoklar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tüm Stoklar</SelectItem>
                        {uniqueStockAmounts.map(amount => (
                          <SelectItem key={amount} value={String(amount)}>{amount} adet</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Stok Durumu Butonları */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Stok Durumu:</span>
                  <Button 
                    size="sm" 
                    variant={productStockStatus === 'all' ? 'default' : 'outline'}
                    onClick={() => setProductStockStatus('all')}
                    className="text-xs"
                  >
                    Tümü
                  </Button>
                  <Button 
                    size="sm" 
                    variant={productStockStatus === 'inStock' ? 'default' : 'outline'}
                    onClick={() => setProductStockStatus('inStock')}
                    className="text-xs bg-green-600 hover:bg-green-700"
                  >
                    Stokta Var
                  </Button>
                  <Button 
                    size="sm" 
                    variant={productStockStatus === 'outOfStock' ? 'default' : 'outline'}
                    onClick={() => setProductStockStatus('outOfStock')}
                    className="text-xs bg-red-600 hover:bg-red-700"
                  >
                    Stokta Yok
                  </Button>
                </div>
                
                {/* Toplu Görünürlük Ayarları */}
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={selectedProducts.length === filteredProductsForManagement.length && filteredProductsForManagement.length > 0}
                        onChange={(e) => handleSelectAllProducts(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm font-medium text-purple-700">Tümünü Seç ({selectedProducts.length}/{filteredProductsForManagement.length})</span>
                    </label>
                    
                    {selectedProducts.length > 0 && (
                      <>
                        <div className="h-6 w-px bg-purple-300" />
                        <span className="text-sm text-purple-600">Toplu Görünürlük:</span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleBulkVisibilityUpdate('visible_bireysel', true)}
                          className="text-xs border-blue-400 text-blue-600 hover:bg-blue-50"
                        >
                          Bireysel ✓
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleBulkVisibilityUpdate('visible_bireysel', false)}
                          className="text-xs border-blue-400 text-blue-600 hover:bg-blue-50"
                        >
                          Bireysel ✗
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleBulkVisibilityUpdate('visible_toptanci', true)}
                          className="text-xs border-green-400 text-green-600 hover:bg-green-50"
                        >
                          Toptancı ✓
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleBulkVisibilityUpdate('visible_toptanci', false)}
                          className="text-xs border-green-400 text-green-600 hover:bg-green-50"
                        >
                          Toptancı ✗
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                {(productSearchTerm || productStockFilter !== 'all' || productStockStatus !== 'all') && (
                  <div className="text-sm text-gray-600">
                    <Badge variant="outline">{filteredProductsForManagement.length}</Badge> ürün bulundu
                  </div>
                )}
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-blue-800 text-xs sm:text-sm">
                    Excel formatı: Ürün Kodu;Ürün Adı;Marka;DOT;Mevsim;Fiyat;İskonto1;İskonto2;İskonto3;İskonto4;{warehouses.map(w => `${w.name} Stok`).join(';') || 'Depo Stok'}
                  </AlertDescription>
                </Alert>
              </div>

              {/* Ürün Listesi */}
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                      <Table className="min-w-[800px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10">
                              <input 
                                type="checkbox" 
                                checked={selectedProducts.length === filteredProductsForManagement.length && filteredProductsForManagement.length > 0}
                                onChange={(e) => handleSelectAllProducts(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300"
                              />
                            </TableHead>
                            {showProductImages && <TableHead className="w-16">Resim</TableHead>}
                            <TableHead>Ürün Kodu</TableHead>
                            <TableHead>Ürün</TableHead>
                            <TableHead>Marka</TableHead>
                            <TableHead>Mevsim</TableHead>
                            <TableHead>Fiyat</TableHead>
                            <TableHead>Stok</TableHead>
                            <TableHead className="w-28">İşlemler</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredProductsForManagement.map(p => {
                              const basePrice = p.base_price || 0;
                              const i1 = p.iskonto1 || 0;
                              const i2 = p.iskonto2 || 0;
                              const i3 = p.iskonto3 || 0;
                              const i4 = p.iskonto4 || 0;
                              const isSelected = selectedProducts.includes(p.id);
                              
                              // İskonto hesaplamaları
                              const i1Price = i1 ? basePrice * (1 - i1/100) : null;
                              const i2Price = (i1 && i2) ? basePrice * (1 - i1/100) * (1 - i2/100) : null;
                              const i3Price = (i1 && i3) ? basePrice * (1 - i1/100) * (1 - i3/100) : null;
                              // i4 ana fiyattan hesaplanacak (i1 üzerinden değil)
                              const i4Price = i4 ? basePrice * (1 - i4/100) : null;
                              
                              // Tabloda gösterilecek fiyat mantığı:
                              // 1. i1+i2+i3 doluysa → i2 göster
                              // 2. i1 doluysa → i1 göster
                              // 3. iskonto yoksa → ana fiyat göster (yeşil olarak)
                              let displayPrice = basePrice; // varsayılan olarak ana fiyat
                              if (i1 && i2 && i3) {
                                displayPrice = i2Price; // i1+i2+i3 doluysa i2 göster
                              } else if (i1) {
                                displayPrice = i1Price; // i1 doluysa i1 göster (i4 olsa bile)
                              }
                              
                              const totalStock = productWarehouseFilter === 'all' 
                                ? Object.values(p.stock || {}).reduce((a, b) => a + b, 0)
                                : (p.stock?.[productWarehouseFilter] || 0);
                              const isExpanded = expandedProductId === p.id;

                              return (
                                <React.Fragment key={p.id}>
                                  <TableRow className={`cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-purple-50' : ''}`} onClick={() => setExpandedProductId(isExpanded ? null : p.id)}>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                      <input 
                                        type="checkbox" 
                                        checked={isSelected}
                                        onChange={(e) => handleSelectProduct(p.id, e.target.checked)}
                                        className="w-4 h-4 rounded border-gray-300"
                                      />
                                    </TableCell>
                                    {showProductImages && (
                                      <TableCell>
                                        <img src={p.image || 'https://via.placeholder.com/48?text=Lastik'} alt={p.name} className="w-12 h-12 object-contain bg-gray-100 rounded" onError={(e) => { e.target.src = 'https://via.placeholder.com/48?text=Lastik'; }} />
                                      </TableCell>
                                    )}
                                    <TableCell className="font-mono text-sm">{p.code}</TableCell>
                                    <TableCell className="max-w-xs truncate">{p.name}</TableCell>
                                    <TableCell>{p.brand}</TableCell>
                                    <TableCell><Badge>{p.season}</Badge></TableCell>
                                    <TableCell>
                                      <div className="flex flex-col">
                                        <span className="line-through text-gray-400 text-sm">{formatCurrency(basePrice)}</span>
                                        <span className="text-green-600 font-semibold">{formatCurrency(displayPrice)}</span>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant={totalStock > 0 ? 'default' : 'secondary'}>{totalStock} adet</Badge>
                                    </TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                      <div className="flex gap-1">
                                        <Dialog>
                                          <DialogTrigger asChild>
                                            <Button size="sm" variant="outline" onClick={() => setEditingProduct({ ...p })}><Edit className="w-4 h-4" /></Button>
                                          </DialogTrigger>
                                          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                            <DialogHeader><DialogTitle>Ürün Düzenle</DialogTitle></DialogHeader>
                                            {editingProduct && (
                                      <div className="space-y-4 py-4">
                                        <div className="grid grid-cols-2 gap-4">
                                          <div className="space-y-2"><Label>Ürün Kodu</Label><Input value={editingProduct.code || ''} onChange={(e) => setEditingProduct({ ...editingProduct, code: e.target.value })} /></div>
                                          <div className="space-y-2"><Label>Marka</Label><Input value={editingProduct.brand || ''} onChange={(e) => setEditingProduct({ ...editingProduct, brand: e.target.value })} /></div>
                                        </div>
                                        <div className="space-y-2"><Label>Ürün Adı</Label><Input value={editingProduct.name || ''} onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })} /></div>
                                        <div className="grid grid-cols-3 gap-4">
                                          <div className="space-y-2"><Label>DOT</Label><Input value={editingProduct.dot || ''} onChange={(e) => setEditingProduct({ ...editingProduct, dot: e.target.value })} /></div>
                                          <div className="space-y-2"><Label>Mevsim</Label>
                                            <Select value={editingProduct.season} onValueChange={(v) => setEditingProduct({ ...editingProduct, season: v })}>
                                              <SelectTrigger><SelectValue /></SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="Yaz">Yaz</SelectItem>
                                                <SelectItem value="Kış">Kış</SelectItem>
                                                <SelectItem value="4 Mevsim">4 Mevsim</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                          <div className="space-y-2"><Label>Ana Fiyat</Label><Input type="number" value={editingProduct.base_price || ''} onChange={(e) => setEditingProduct({ ...editingProduct, base_price: parseFloat(e.target.value) || 0 })} /></div>
                                        </div>
                                        <div className="space-y-2"><Label>Ürün Resmi URL</Label><Input value={editingProduct.image || ''} onChange={(e) => setEditingProduct({ ...editingProduct, image: e.target.value })} /></div>
                                        <div className="grid grid-cols-4 gap-4">
                                          <div className="space-y-2"><Label>İskonto 1</Label><Input type="number" value={editingProduct.iskonto1 || 0} onChange={(e) => setEditingProduct({ ...editingProduct, iskonto1: parseFloat(e.target.value) || 0 })} /></div>
                                          <div className="space-y-2"><Label>İskonto 2</Label><Input type="number" value={editingProduct.iskonto2 || 0} onChange={(e) => setEditingProduct({ ...editingProduct, iskonto2: parseFloat(e.target.value) || 0 })} /></div>
                                          <div className="space-y-2"><Label>İskonto 3</Label><Input type="number" value={editingProduct.iskonto3 || 0} onChange={(e) => setEditingProduct({ ...editingProduct, iskonto3: parseFloat(e.target.value) || 0 })} /></div>
                                          <div className="space-y-2"><Label>İskonto 4</Label><Input type="number" value={editingProduct.iskonto4 || 0} onChange={(e) => setEditingProduct({ ...editingProduct, iskonto4: parseFloat(e.target.value) || 0 })} /></div>
                                        </div>
                                        {/* Kargo Ücreti */}
                                        <div className="space-y-2">
                                          <Label>Kargo Ücreti (Fiyata eklenir)</Label>
                                          <Input type="number" value={editingProduct.shipping_cost || 0} onChange={(e) => setEditingProduct({ ...editingProduct, shipping_cost: parseFloat(e.target.value) || 0 })} placeholder="0" />
                                          <p className="text-xs text-gray-500">Bu tutar kullanıcıya gösterilen fiyata otomatik eklenir</p>
                                        </div>
                                        {/* Görünürlük Ayarları */}
                                        <div className="space-y-2">
                                          <Label>Görünürlük Ayarları</Label>
                                          <div className="flex flex-wrap gap-4">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                              <input 
                                                type="checkbox" 
                                                checked={editingProduct.visible_bireysel === true}
                                                onChange={(e) => setEditingProduct({ ...editingProduct, visible_bireysel: e.target.checked })}
                                                className="w-4 h-4 rounded border-gray-300"
                                              />
                                              <span className="text-sm">Bireysel Kullanıcılara Göster</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                              <input 
                                                type="checkbox" 
                                                checked={editingProduct.visible_toptanci === true}
                                                onChange={(e) => setEditingProduct({ ...editingProduct, visible_toptanci: e.target.checked })}
                                                className="w-4 h-4 rounded border-gray-300"
                                              />
                                              <span className="text-sm">Toptancılara Göster</span>
                                            </label>
                                          </div>
                                        </div>
                                        {/* Depo Bazlı Görünürlük */}
                                        <div className="space-y-2">
                                          <Label>Depo Bazlı Görünürlük</Label>
                                          <p className="text-xs text-gray-500 mb-2">Kullanıcı tipine göre ürünün hangi depodan gösterileceğini seçin</p>
                                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                              <Label className="text-sm text-blue-600">Bireysel Kullanıcılar İçin Depo</Label>
                                              <Select 
                                                value={editingProduct.depot_bireysel || 'all'} 
                                                onValueChange={(v) => setEditingProduct({ ...editingProduct, depot_bireysel: v })}
                                              >
                                                <SelectTrigger><SelectValue placeholder="Depo Seçin" /></SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="all">Tüm Depolar</SelectItem>
                                                  {warehouses.map(wh => (
                                                    <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </div>
                                            <div className="space-y-2">
                                              <Label className="text-sm text-green-600">Toptancılar İçin Depo</Label>
                                              <Select 
                                                value={editingProduct.depot_toptanci || 'all'} 
                                                onValueChange={(v) => setEditingProduct({ ...editingProduct, depot_toptanci: v })}
                                              >
                                                <SelectTrigger><SelectValue placeholder="Depo Seçin" /></SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="all">Tüm Depolar</SelectItem>
                                                  {warehouses.map(wh => (
                                                    <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="space-y-2">
                                          <Label>Depo Stokları</Label>
                                          <div className="grid grid-cols-2 gap-2">
                                            {warehouses.map(wh => (
                                              <div key={wh.id} className="flex items-center gap-2">
                                                <Label className="w-24 text-sm">{wh.name}:</Label>
                                                <Input type="number" className="w-20" value={editingProduct.stock?.[wh.id] || 0} onChange={(e) => setEditingProduct({ ...editingProduct, stock: { ...editingProduct.stock, [wh.id]: parseInt(e.target.value) || 0 } })} />
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    <DialogFooter>
                                      <Button variant="outline" onClick={() => setEditingProduct(null)}>İptal</Button>
                                      <Button onClick={handleUpdateProduct}>Kaydet</Button>
                                    </DialogFooter>
                                  </DialogContent>
                                </Dialog>
                                <Button size="sm" variant="destructive" onClick={() => handleDeleteProduct(p.id)}><Trash2 className="w-4 h-4" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          {/* İskonto Paneli - Açılır Çekmece */}
                          {isExpanded && (
                            <TableRow className="bg-gray-50">
                              <TableCell colSpan={8} className="p-4">
                                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
                                  <div className="p-3 bg-white rounded-lg border">
                                    <p className="text-xs text-gray-500 mb-1">Ana Fiyat</p>
                                    <p className="text-lg font-bold line-through text-gray-400">{formatCurrency(basePrice)}</p>
                                  </div>
                                  {i1 > 0 && (
                                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                      <p className="text-xs text-blue-600 mb-1">İskonto 1 (%{i1})</p>
                                      <p className="text-lg font-bold text-blue-700">{formatCurrency(i1Price)}</p>
                                      <p className="text-xs text-gray-500">Bireysel (i1+i4 veya sadece i1)</p>
                                    </div>
                                  )}
                                  {i2Price && (
                                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                      <p className="text-xs text-green-600 mb-1">İskonto 2 (%{i2})</p>
                                      <p className="text-lg font-bold text-green-700">{formatCurrency(i2Price)}</p>
                                      <p className="text-xs text-gray-500">Bireysel (i1+i2+i3)</p>
                                    </div>
                                  )}
                                  {i3Price && (
                                    <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                                      <p className="text-xs text-orange-600 mb-1">İskonto 3 (%{i3})</p>
                                      <p className="text-lg font-bold text-orange-700">{formatCurrency(i3Price)}</p>
                                      <p className="text-xs text-gray-500">Toptancı (i1+i2+i3)</p>
                                    </div>
                                  )}
                                  {i4Price && (
                                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                                      <p className="text-xs text-purple-600 mb-1">İskonto 4 (%{i4}) - Ana Fiyattan</p>
                                      <p className="text-lg font-bold text-purple-700">{formatCurrency(i4Price)}</p>
                                      <p className="text-xs text-gray-500">Toptancı (i1+i4)</p>
                                    </div>
                                  )}
                                </div>
                                {/* Depo Stokları */}
                                <div className="mt-4 pt-4 border-t">
                                  <p className="text-sm font-medium text-gray-700 mb-2">Depo Stokları:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {warehouses.map(wh => (
                                      <Badge key={wh.id} variant={p.stock?.[wh.id] > 0 ? 'default' : 'secondary'}>
                                        {wh.name}: {p.stock?.[wh.id] || 0} adet
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                      </TableBody>
                    </Table>
                  {products.length === 0 && (
                    <div className="text-center py-12"><Package className="w-12 h-12 mx-auto text-gray-300 mb-3" /><p className="text-gray-500">Ürün bulunamadı.</p></div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Sayım */}
          {activeTab === 'sayim' && (
            <SayimSection 
              warehouses={warehouses}
              products={products}
              sayimWarehouse={sayimWarehouse}
              setSayimWarehouse={setSayimWarehouse}
              sayimSearch={sayimSearch}
              setSayimSearch={setSayimSearch}
              sayimData={sayimData}
              setSayimData={setSayimData}
              sayimQuickAdd={sayimQuickAdd}
              setSayimQuickAdd={setSayimQuickAdd}
            />
          )}

          {/* Fiyatlama */}
          {activeTab === 'fiyatlama' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Marka Bazlı İskonto Yönetimi</h2>
                <Badge variant="outline" className="text-sm">
                  {Object.keys(brandCategories).length} Marka • {products.length} Ürün
                </Badge>
              </div>

              {/* Açıklama */}
              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription className="text-blue-800">
                  Ürünler markaya ve parantez içindeki kategorilere göre otomatik gruplandı. 
                  Örn: LASSA (KAMYON), DAYTON (TİCARİ), BRIDGESTONE (PERFORMANS) gibi. 
                  Her kategori için toplu iskonto uygulayabilirsiniz.
                </AlertDescription>
              </Alert>

              {/* İskonto Mantığı */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-green-800 mb-2">Bireysel Fiyat:</h4>
                    <p className="text-sm text-green-700">i1+i2+i3 doluysa → İskonto 2 fiyatı</p>
                    <p className="text-sm text-green-700">i1+i4 doluysa → İskonto 1 fiyatı</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-orange-800 mb-2">Toptancı Fiyat:</h4>
                    <p className="text-sm text-orange-700">i1+i2+i3 doluysa → İskonto 3 fiyatı</p>
                    <p className="text-sm text-orange-700">i1+i4 doluysa → İskonto 4 fiyatı</p>
                  </CardContent>
                </Card>
              </div>

              {/* Marka Listesi */}
              {Object.keys(brandCategories).length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">Henüz ürün yok. Önce ürün ekleyin.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {Object.entries(brandCategories)
                    .sort((a, b) => b[1].totalProducts - a[1].totalProducts)
                    .map(([brand, data]) => (
                    <Card key={brand} className="overflow-hidden">
                      <div 
                        className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 flex items-center justify-between"
                        onClick={() => setPricingExpandedBrand(pricingExpandedBrand === brand ? null : brand)}
                      >
                        <div className="flex items-center gap-3">
                          <ChevronDown className={`w-5 h-5 transition-transform ${pricingExpandedBrand === brand ? 'rotate-180' : ''}`} />
                          <h3 className="text-lg font-bold">{brand}</h3>
                          <Badge>{data.totalProducts} ürün</Badge>
                          <Badge variant="outline">{Object.keys(data.subcategories).length} kategori</Badge>
                        </div>
                      </div>
                      
                      {pricingExpandedBrand === brand && (
                        <CardContent className="p-4 space-y-4">
                          {Object.entries(data.subcategories)
                            .sort((a, b) => b[1].products.length - a[1].products.length)
                            .map(([subcat, subcatData]) => {
                              // Get current discount values from first product in category
                              const sampleProduct = subcatData.products[0] || {};
                              const currentI1 = sampleProduct.iskonto1 || 0;
                              const currentI2 = sampleProduct.iskonto2 || 0;
                              const currentI3 = sampleProduct.iskonto3 || 0;
                              const currentI4 = sampleProduct.iskonto4 || 0;
                              
                              const stateKey = `${brand}_${subcat}`;
                              const localState = pricingBrandDiscounts[stateKey] || {
                                i1: currentI1, i2: currentI2, i3: currentI3, i4: currentI4
                              };
                              
                              return (
                                <div key={subcat} className="border rounded-lg p-4 bg-white">
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                                    <div className="flex items-center gap-2">
                                      <h4 className="font-semibold text-gray-800">
                                        {brand} ({subcat})
                                      </h4>
                                      <Badge variant="secondary">{subcatData.products.length} ürün</Badge>
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                                    <div className="space-y-1">
                                      <Label className="text-xs">İskonto 1 (%)</Label>
                                      <Input 
                                        type="number" 
                                        value={localState.i1}
                                        onChange={(e) => setPricingBrandDiscounts({
                                          ...pricingBrandDiscounts,
                                          [stateKey]: { ...localState, i1: e.target.value }
                                        })}
                                        className="h-9"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">İskonto 2 (%) <span className="text-green-600">Bireysel</span></Label>
                                      <Input 
                                        type="number" 
                                        value={localState.i2}
                                        onChange={(e) => setPricingBrandDiscounts({
                                          ...pricingBrandDiscounts,
                                          [stateKey]: { ...localState, i2: e.target.value }
                                        })}
                                        className="h-9"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">İskonto 3 (%) <span className="text-orange-600">Toptancı</span></Label>
                                      <Input 
                                        type="number" 
                                        value={localState.i3}
                                        onChange={(e) => setPricingBrandDiscounts({
                                          ...pricingBrandDiscounts,
                                          [stateKey]: { ...localState, i3: e.target.value }
                                        })}
                                        className="h-9"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">İskonto 4 (%)</Label>
                                      <Input 
                                        type="number" 
                                        value={localState.i4}
                                        onChange={(e) => setPricingBrandDiscounts({
                                          ...pricingBrandDiscounts,
                                          [stateKey]: { ...localState, i4: e.target.value }
                                        })}
                                        className="h-9"
                                      />
                                    </div>
                                  </div>
                                  
                                  <Button 
                                    size="sm"
                                    className="w-full sm:w-auto"
                                    onClick={() => applyAllDiscountsToCategory(
                                      brand, 
                                      subcat, 
                                      localState.i1, 
                                      localState.i2, 
                                      localState.i3, 
                                      localState.i4
                                    )}
                                  >
                                    <Save className="w-4 h-4 mr-2" />
                                    Bu Kategoriye Uygula ({subcatData.products.length} ürün)
                                  </Button>
                                </div>
                              );
                            })}
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Depo Yönetimi */}
          {activeTab === 'depolar' && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-800">Depo Yönetimi</h2>
              <Card>
                <CardHeader><CardTitle>Depolar</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-4">
                    <Input placeholder="Yeni depo adı" value={newWarehouse} onChange={(e) => setNewWarehouse(e.target.value)} className="max-w-xs" />
                    <Button onClick={handleAddWarehouse}><Plus className="w-4 h-4 mr-2" /> Ekle</Button>
                  </div>
                  <Table>
                    <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Depo Adı</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {warehouses.map(wh => (
                        <TableRow key={wh.id}>
                          <TableCell>{wh.id}</TableCell>
                          <TableCell className="font-medium">{wh.name}</TableCell>
                          <TableCell><Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteWarehouse(wh.id)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {warehouses.length === 0 && <div className="text-center py-8"><p className="text-gray-500">Depo bulunamadı.</p></div>}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Hesap Numaraları */}
          {activeTab === 'hesaplar' && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-800">Banka Hesap Numaraları</h2>
              <Card>
                <CardHeader><CardTitle>Yeni Hesap Ekle</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2"><Label>Banka Adı *</Label><Input value={newBankAccount.bank_name} onChange={(e) => setNewBankAccount({ ...newBankAccount, bank_name: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Şube</Label><Input value={newBankAccount.branch} onChange={(e) => setNewBankAccount({ ...newBankAccount, branch: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Hesap Adı</Label><Input value={newBankAccount.account_name} onChange={(e) => setNewBankAccount({ ...newBankAccount, account_name: e.target.value })} /></div>
                    <div className="space-y-2"><Label>IBAN *</Label><Input value={newBankAccount.iban} onChange={(e) => setNewBankAccount({ ...newBankAccount, iban: e.target.value })} /></div>
                  </div>
                  <Button onClick={handleAddBankAccount}><Plus className="w-4 h-4 mr-2" /> Hesap Ekle</Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Mevcut Hesaplar</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow><TableHead>Banka</TableHead><TableHead>Şube</TableHead><TableHead>IBAN</TableHead><TableHead>Durum</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {bankAccounts.map(acc => (
                        <TableRow key={acc.id}>
                          <TableCell className="font-medium">{acc.bank_name}</TableCell>
                          <TableCell>{acc.branch}</TableCell>
                          <TableCell className="font-mono text-sm">{acc.iban}</TableCell>
                          <TableCell><div className="flex items-center space-x-2"><Switch checked={acc.is_active} onCheckedChange={() => handleToggleBankAccount(acc.id)} /><span className="text-sm">{acc.is_active ? 'Aktif' : 'Pasif'}</span></div></TableCell>
                          <TableCell><Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteBankAccount(acc.id)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Online Ödeme */}
          {activeTab === 'onlineodeme' && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-800">Online Ödeme Ayarları</h2>
              <Card>
                <CardHeader><CardTitle>Ödeme Linki ve Şifre</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-4 mb-4">
                    <Switch checked={onlinePayment.is_active} onCheckedChange={(checked) => setOnlinePayment({ ...onlinePayment, is_active: checked })} />
                    <Label>Online Ödeme {onlinePayment.is_active ? 'Aktif' : 'Pasif'}</Label>
                  </div>
                  <div className="space-y-2"><Label>Ödeme Linki</Label><Input value={onlinePayment.link} onChange={(e) => setOnlinePayment({ ...onlinePayment, link: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Şifre</Label><Input value={onlinePayment.password} onChange={(e) => setOnlinePayment({ ...onlinePayment, password: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Açıklama</Label><Textarea value={onlinePayment.description} onChange={(e) => setOnlinePayment({ ...onlinePayment, description: e.target.value })} rows={3} /></div>
                  <Button className="w-full" onClick={handleSaveOnlinePayment}><Save className="w-4 h-4 mr-2" /> Kaydet</Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Kredi Kartı Sistemleri */}
          {activeTab === 'kredikarti' && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-800">Kredi Kartı Sistemleri</h2>
              <Card>
                <CardHeader><CardTitle>Yeni Sistem Ekle</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label>Sistem Adı *</Label>
                      <Select value={newCreditCardSystem.name} onValueChange={(v) => setNewCreditCardSystem({ ...newCreditCardSystem, name: v })}>
                        <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="iyzico">iyzico</SelectItem>
                          <SelectItem value="PayTR">PayTR</SelectItem>
                          <SelectItem value="Param">Param</SelectItem>
                          <SelectItem value="Paratika">Paratika</SelectItem>
                          <SelectItem value="Sipay">Sipay</SelectItem>
                          <SelectItem value="Moka">Moka</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>API Key</Label><Input value={newCreditCardSystem.api_key} onChange={(e) => setNewCreditCardSystem({ ...newCreditCardSystem, api_key: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Secret Key</Label><Input type="password" value={newCreditCardSystem.secret_key} onChange={(e) => setNewCreditCardSystem({ ...newCreditCardSystem, secret_key: e.target.value })} /></div>
                  </div>
                  <Button onClick={handleAddCreditCardSystem}><Plus className="w-4 h-4 mr-2" /> Sistem Ekle</Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Mevcut Sistemler</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow><TableHead>Sistem</TableHead><TableHead>API Key</TableHead><TableHead>Durum</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {creditCardSystems.map(sys => (
                        <TableRow key={sys.id}>
                          <TableCell className="font-medium">{sys.name}</TableCell>
                          <TableCell className="font-mono text-sm">{sys.api_key ? '••••••••' : '-'}</TableCell>
                          <TableCell><div className="flex items-center space-x-2"><Switch checked={sys.is_active} onCheckedChange={() => handleToggleCreditCardSystem(sys.id)} /><span className="text-sm">{sys.is_active ? 'Aktif' : 'Pasif'}</span></div></TableCell>
                          <TableCell><Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteCreditCardSystem(sys.id)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Ayarlar */}
          {activeTab === 'ayarlar' && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-800">Admin Ayarları</h2>
              <Card>
                <CardHeader><CardTitle>Hesap Bilgileri</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2"><Label>Kullanıcı Adı</Label><Input value={adminSettings.username} onChange={(e) => setAdminSettings({ ...adminSettings, username: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Yeni Şifre</Label><Input type="password" value={adminSettings.newPassword} onChange={(e) => setAdminSettings({ ...adminSettings, newPassword: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Yeni Şifre (Tekrar)</Label><Input type="password" value={adminSettings.confirmPassword} onChange={(e) => setAdminSettings({ ...adminSettings, confirmPassword: e.target.value })} /></div>
                  <Button className="w-full" onClick={handleSaveAdminSettings}><Save className="w-4 h-4 mr-2" /> Kaydet</Button>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
